import {
    AddPointFieldCustomization,
    AddTagRequest,
    AdditionalPointDetails,
    CreateLocationRequest,
    CreatePointRequest,
    CustomFieldCustomization,
    GeoJsonType,
    GetPointResponse,
    GetPublicOrganizationProductResponse,
    Image,
    ImageUpload,
    IntegrationException,
    LatLng,
    Location,
    MapDrawer,
    ObjectId,
    PaymentTargetType,
    PointFieldEntry,
    PointPassthroughParameter,
    TopLevelShapeHolder,
} from '../types';
import { DEFAULT_ICON_SIZE, DUMMY_DATE, ImageSizingGuide, ZERO_OBJECT_ID } from '../helpers/constants';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { addShapeToMap, countVertices, getShapeIconName, makeShapeEditable, removeShapeFromMap, showShape } from '../helpers/shapes';
import { blobToDataUrl, rotateImage } from '../helpers/files';
import { checkCustomFieldVisibility, checkDateTimeFieldVisibility, checkDescriptionFieldVisibility, checkEmailFieldVisibility, checkMoreInfoFieldVisibility, checkPhotosFieldVisibility, checkProductVisibility, checkSpecialOfferFieldVisibility, checkTagsFieldVisibility, checkTermsFieldVisibility, checkTitleFieldVisibility } from '../helpers/custom-form';
import { convertLocaleToUTC, convertUTCToLocale, getDateTimeStringForPoint } from '../helpers/date';
import { customElement, property, query, state } from 'lit/decorators';
import { delay, getFromLocalStorage, getMarkerElement, isDefined, isValidString, removeLocalStorage, setLocalStorage, setMarkerMap, show_confetti, undefinedIfEmpty, updateMarkerVisibility } from '../helpers/utils';
import { formatPhoneNumberAsUserTypes, pointNumberValid, validateEmail, validateUrl } from '../helpers/strings';
import { isLocationInputLatLong, timezoneAsync } from '../helpers/api/google-api';
import { logWithAmplitude } from '../helpers/api/api';

import { CategorySelector } from './theme/category-selector';
import { ConfirmDialogElement } from './confirm-dialog';
import { DateTimeRangePickerComponent } from './common/date-time-range-picker';
import { EditShapeModal } from './edit-shape-modal';
import { FormCheckbox } from './theme/form-checkbox';
import { Marker } from '@twitchaxorg/markerclusterer';
import { PreCheckoutComponent } from './pre-checkout-component';
import { ProxiInputPlace } from './common/proxi-input-place';
import { ProxiTextfield } from './theme/proxi-textfield';
import { RichTextEditor } from './common/rich-text-editor';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TEMPLATE_CATEGORIES } from '../helpers/api/topics-api';
import TurndownService from 'turndown';
import accountInfo from '../helpers/state/account-info';
import center from '@turf/center';
import drawingTool from '../helpers/state/drawing-tool';
import featuresInfo from '../helpers/state/features-info';
import format from 'date-fns/format';
import { geometry } from '@turf/helpers';
import { getMainPage } from '../helpers/page-name';
import { getOrganizationProductsPublic } from '../helpers/api/products-api';
import { getPointImages, getPointListingSync } from '../helpers/api/points-api';
import { getPreviewIcon } from '../helpers/markers';
import { getQueryStringParameters } from '../helpers/urls';
import { ifDefined } from 'lit/directives/if-defined';
import { makeColorsIntoNumberSvg } from '../helpers/colors';
import { marked } from 'marked';
import { palette } from '../style/variables';
import { parseDescriptionText, renderEmbedlyCards } from '../helpers/embeds';
import piexif from 'piexifjs';
import pointCache from '../helpers/state/point-cache';
import { removeInertFromVaadinOverlay } from '../helpers/inert';
import { saveEmail } from '../helpers/api/account-api';
import { startStripeCheckoutSession } from '../helpers/api/stripe-api';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';
import { unsafeHTML } from 'lit/directives/unsafe-html';

@customElement('add-edit-point-component')
export class AddEditPointComponent extends LitElement {
    static amplitude_callsite: string = 'Add/Edit Point Modal';

    @query('#categorySelector')
    private readonly categorySelectorElement?: CategorySelector;

    @query('#email')
    private readonly emailElement?: ProxiTextfield;

    @query('#location')
    private readonly locationElement!: ProxiInputPlace;

    @query('#number')
    private readonly numberElement?: ProxiTextfield | null;

    @query('#name')
    private readonly nameElement?: ProxiTextfield | null;

    @query('#url')
    private readonly urlElement?: ProxiTextfield | null;

    @query('#description')
    private readonly descriptionElement?: RichTextEditor | null;

    @query('#special_offer_description')
    private readonly specialOfferDescriptionElement?: HTMLInputElement | null;

    @query('#fileInput')
    private readonly fileInput?: HTMLInputElement | null;

    @query('#termsAndConditionsCheckbox')
    private readonly termsAndConditionsCheckboxElement?: FormCheckbox;

    @query('#keepPreviousPlaceDetailsCheckbox')
    private readonly keepPreviousPlaceDetailsCheckboxElement?: FormCheckbox;

    @query('#deleteConfirm')
    private readonly deleteConfirmElement?: ConfirmDialogElement;

    @query('#dateTimeRangePicker')
    private readonly dateTimeRangePickerElement?: DateTimeRangePickerComponent | null;

    @query('#shapeStyleEditor')
    private readonly shapeStyleEditorElement?: EditShapeModal | null;

    @query('#preCheckout')
    private readonly preCheckoutElement?: PreCheckoutComponent | null;

    @property({ type: Boolean })
    private readonly isPointPage: boolean = false;

    @property({ type: Boolean })
    private readonly isNominationPage: boolean = false;

    @property({ type: String })
    private readonly nomination_category_id?: string;

    @state()
    private success: boolean = false;

    @state()
    private uploadInProgress: boolean = false;

    @state()
    private validationInProgress: boolean = false;

    @state()
    rotateValue: number = 0;

    @state()
    rotatePreviewCSS: string = '';

    @state()
    image?: ImageUpload;

    @state()
    isAlreadyOnMap: boolean = false;

    @state()
    pointsAlreadyOnMap: GetPointResponse[] = [];

    @property({ type: Boolean })
    editMode: boolean = true;

    @property({ type: Boolean })
    bypassGoogleKey: boolean = false;

    @property({ type: Boolean })
    hideTitle: boolean = false;

    @state()
    activeTab: string = 'category';

    @state()
    iconPreview?: string;

    @state()
    temporaryIcon?: Marker;

    @state()
    temporaryCircle?: Marker;

    // eslint-disable-next-line accessor-pairs
    @property({ type: Object })
    set userSelectedLatLng(l: Location | undefined) {
        if (l === undefined || (l.latLng.lat === 0 && l.latLng.lng === 0) || l.latLng.lat === undefined || l.latLng.lng === undefined) {
            this.resetLocationValue();
            return;
        }
        setTimeout(() => this.setUserSelectedLatLng(l), 100);
    }

    // eslint-disable-next-line accessor-pairs
    @property({ type: Object })
    set userDrawnShapeGeometry(g: TopLevelShapeHolder | undefined) {
        if (g === undefined) {
            // this.customShape = undefined;
            // this.userDrawnShapeGeometry = undefined;
            return;
        }
        setTimeout(() => this.setuserDrawnShapeGeometry(g), 100);
    }

    // @property({ type: Object })
    // userDrawnShapeGeometry: GeoJsonGeometry | undefined;

    @property({ type: Object })
    point?: GetPointResponse;

    @state()
    customShape?: TopLevelShapeHolder;

    @state()
    topicCategoryId?: string;

    @state()
    uploadedImages: ImageUpload[] = [];

    @state()
    existingImages: Image[] = [];

    @state()
    imagesToDelete: Image[] = [];

    @state()
    hasSpecialOffer?: boolean;

    @state()
    selectedTags: ObjectId[] = [];

    @state()
    rerenderTags: boolean = false;

    @state()
    termsAndConditionsChecked: boolean = false;

    @state()
    allNumbersCollection: Array<string | undefined> = [];

    @state()
    userInputtedLatLng?: LatLng;

    @state()
    locationBeforeDrag?: Location;

    @state()
    dragEndView: boolean = false;

    @state()
    selectedOptions: Map<string, string[]> = new Map();

    @state()
    listingFieldValues: Map<string, string> = new Map();

    @state()
    descriptionPreviewMode: boolean = false;

    @state()
    previewMode: boolean = false;

    @state()
    previewDescriptionText: string = '';

    @state()
    timezoneShortName?: string;

    /**
     * Full IANA timezone the point's location resolves to (e.g.
     * "America/Los_Angeles"). Shown verbatim in the form so the
     * submitter can confirm what zone the times they type will be
     * saved in — the picker's existing "(PDT)" header is a nice
     * shorthand but doesn't always pin down which America/X zone is
     * meant (Phoenix vs Denver, Indianapolis vs New York, etc).
     */
    @state()
    timezoneId?: string;

    /**
     * Live preview of how the picked start/end will appear on the
     * point card. Recomputed whenever the picker fires a change so
     * admins can sanity-check the format without saving and reopening.
     * Stays undefined when the picker is empty or while the location's
     * tz lookup is still in flight.
     */
    @state()
    timeRangePreview?: string;

    @state()
    custom_icon_url?: string;

    // this is for a topic exception for an integration. do not use this for other purposes
    @state()
    exceptionHideCustomFields: boolean = false;

    @state()
    number?: string;

    @state()
    showShapeEditor: boolean = false;

    @state()
    dontSaveEmailAfterSubmit: boolean = false;

    @state()
    previousLocationValue: Location = {
        search: '',
        place_id: '',
        latLng: {
            lat: 0,
            lng: 0
        },
    };

    @state()
    formProducts: GetPublicOrganizationProductResponse[] = [];

    @state()
    componentNames = {
        titleLabel: 'Title',
        locationPlaceholder: 'e.g. "Place Name" or "123 Main Street',
        moreInfoLabel: 'More Info Link',
        moreInfoPlaceholder: '',
        specialOfferPlaceholder: ''
    };

    @state()
    translatedCustomFields: Array<{originalField: CustomFieldCustomization, translatedField: CustomFieldCustomization | undefined, options?: Array<{text: string; value:string}>}> | undefined;

    private needsMarkerChange: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindFeaturesState = new StateController(this, featuresInfo);

    private _lastKnownTotal: string = '';
    private _totalCheckInterval: number | undefined;

    // [mhaughey] Keep up with total cost changes from pre-checkout component
    // Using polling for now since I'm having trouble getting events to work across components.
    // Could revisit later with a different approach if needed.
    connectedCallback() {
        super.connectedCallback();

        // Poll for changes every 500ms
        this._totalCheckInterval = setInterval(() => {
            if (this.preCheckoutElement) {
                const currentTotal = this.preCheckoutElement.totalCostString;
                if (currentTotal !== this._lastKnownTotal) {
                    this._lastKnownTotal = currentTotal;
                    this.requestUpdate();
                }
            }
        }, 500);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (isDefined(this._totalCheckInterval)) {
            clearInterval(this._totalCheckInterval);
        }
        this.removeTemporaryIcons();
    }

    async firstUpdated(_changedProperties: any) {
        await this.startup();
        await this.removeInertOnDropdown();
        // Add a listener for the 'languageChanged' event
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        translationData.onLanguageChanged(async () => {
            await this.loadTranslationsAndCustomFields();
        });
    }

    async updated(changedProperties: any) {
        // Listen for 'point' changes so we can fill in form.
        changedProperties.forEach(async (oldValue: any, propName: any) => {
            if (propName === 'point') {
                await this.pointChanged();
                this.hasSpecialOffer =
                    this.point != null
                        ? Boolean(
                              this.point.special_offer?.description
                          )
                        : false;
                this.allNumbersCollection = pointCache.allPoints
                    .map((item) => item.number)
                    .filter((item) => !!item)
                    .sort((p1, p2) => (Number(p1 === undefined ? 1000 : p1) - Number(p2 === undefined ? 1000 : p2)))
                    ?? [];
                this.requestUpdate();
            } else if (propName === 'descriptionPreviewMode') {
                if (this.descriptionPreviewMode) {
                    renderEmbedlyCards(this.shadowRoot?.querySelectorAll('a[href*="proxi_embed=true"]') ?? []);
                }
            } else if (propName === 'custom_icon_url' || propName === 'number' || propName === 'topicCategoryId') {
                this.iconPreview = getPreviewIcon(topicData.topic, undefinedIfEmpty(this.custom_icon_url), undefinedIfEmpty(this.numberElement?.value), this.topicCategoryId);
                this.addOrUpdateTemporaryIcons();
                topicData.mapComponent?.requestUpdate();
            }
        });
    }

    get isWriter() : boolean {
        return this.previewMode ? false : topicData.isWriter;
    }

    private adminVisibilityNote(fieldVisibleToUsers: boolean, name: string = 'This field') {
        if (!this.isWriter || fieldVisibleToUsers) return '';
        return html`<span class="admin-visibility-note">${name}: Visible because you are an admin</span>`;
    }

    private isCustomFieldRendered(customField: CustomFieldCustomization): boolean {
        if (this.exceptionHideCustomFields) return false;
        if (customField.archived) return false;
        return checkCustomFieldVisibility(customField, this.currentValues) || this.isWriter;
    }

    get oneCategoryAndSet() {
        return topicData.categories.length === 1 && isDefined(undefinedIfEmpty(this.topicCategoryId));
    }

    async reload() {
        await this.pointChanged();
    }

    private async loadTranslationsAndCustomFields() {
        if (translationData.isActivated) {
            const headerText = topicData.topic?.add_point_customization?.title?.header ?? 'Title';
            const placeholderDefault = 'e.g. "Place Name" or "123 Main Street"';
            const moreInfoLabel = topicData.topic?.add_point_customization?.more_info?.header ?? 'More Info URL';
            const moreInfoPlaceholder = topicData.topic?.add_point_customization?.more_info?.sub_header ?? '';
            const specialOfferPlaceholder = topicData.topic?.add_point_customization?.special_offer?.sub_header ?? '';


            try {
                // Await the promises and update reactive state
                const phrases = [headerText, placeholderDefault, moreInfoLabel, moreInfoPlaceholder, specialOfferPlaceholder];
                const translations = await translationData.translatePhrases(phrases, translationData.language);

                this.componentNames.titleLabel = translations.find(phrase => phrase.original_text === headerText)?.translated_text ?? headerText;
                this.componentNames.locationPlaceholder = translations.find(phrase => phrase.original_text === placeholderDefault)?.translated_text ?? placeholderDefault;
                this.componentNames.moreInfoLabel = translations.find(phrase => phrase.original_text === moreInfoLabel)?.translated_text ?? moreInfoLabel;
                this.componentNames.moreInfoPlaceholder = translations.find(phrase => phrase.original_text === moreInfoPlaceholder)?.translated_text ?? moreInfoPlaceholder;
                this.componentNames.specialOfferPlaceholder = translations.find(phrase => phrase.original_text === specialOfferPlaceholder)?.translated_text ?? specialOfferPlaceholder;
            } catch (error) {
                console.warn('Translation failed, using defaults:', error);
                this.componentNames.titleLabel = headerText;
                this.componentNames.locationPlaceholder = placeholderDefault;
                this.componentNames.moreInfoLabel = moreInfoLabel;
                this.componentNames.moreInfoPlaceholder = moreInfoPlaceholder;
                this.componentNames.specialOfferPlaceholder = specialOfferPlaceholder;
            }

            this.translatedCustomFields = (await Promise.all(
                topicData.topic?.add_point_customization?.custom_fields?.map(async (field) =>
                    await this.getTranslatedCustomField(field)
                ) ?? []
            )).filter((result): result is { originalField: CustomFieldCustomization; translatedField: CustomFieldCustomization; options?: Array<{text: string, value:string}>} =>
                result !== undefined
            );
        } else {
            // Use the original text when translation is not activated
            this.componentNames.titleLabel = topicData.topic?.add_point_customization?.title?.header ?? 'Title';
            this.componentNames.locationPlaceholder = 'e.g. "Place Name" or "123 Main Street"';
            this.componentNames.moreInfoLabel = `${topicData.topic?.add_point_customization?.more_info?.header ?? 'More Info URL'}`;
            this.componentNames.moreInfoPlaceholder = '';
            this.componentNames.specialOfferPlaceholder = `Enter ${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'}`;


            this.translatedCustomFields = topicData.topic?.add_point_customization?.custom_fields?.map((field) => ({
                originalField: field,
                translatedField: undefined,
                options: ('MultiSelect' in field.field_type)  ?
                        field.field_type.MultiSelect.options.map(option => ({ text: option, value: option })) :
                         ('SingleSelect' in field.field_type) ?
                        field.field_type.SingleSelect.options.map(option => ({ text: option, value: option })) : undefined,
            })) ?? [];
        }
    }

    private async startup() {
        this.refreshEmail();
        document.documentElement.style.setProperty('--custom-font', topicData.topic?.custom_font ?? 'Montserrat');
        this.style.setProperty('font-family', topicData.topic?.custom_font ?? 'Montserrat');
        this.style.setProperty('--mdc-typography-button-font-family', topicData.topic?.custom_font ?? 'Montserrat');
        this.allNumbersCollection = pointCache.allPoints
            .map((item) => item.number)
            .filter((item) => !!item)
            .sort((p1, p2) => (Number(p1 === undefined ? 1000 : p1) - Number(p2 === undefined ? 1000 : p2)))
            ?? [];
        if (this.nomination_category_id) {
            this.topicCategoryId = this.nomination_category_id;
        }
        if (topicData.categories.length === 1) {
            this.topicCategoryId = topicData
                .categories[0]._id?.$oid;
        }

        // see if clear emails is in url parameters
        const queryString = window.location.href.split('?')[1];
        const params = new URLSearchParams(queryString);
        this.dontSaveEmailAfterSubmit = params.get('clear_emails') === 'true';
        if (this.dontSaveEmailAfterSubmit && isDefined(this.emailElement)) {
            this.emailElement.value = '';
        } else if (isDefined(this.emailElement)) {
            this.emailElement.value = undefinedIfEmpty(accountInfo.email) ?? getFromLocalStorage('email') ?? this.point?.email ?? '';
        }
        await this.getFormProducts();

        // Load translation and variable names that have to go in components after component initialization
        await this.loadTranslationsAndCustomFields();
    }

    get isConfettiSuppressed() {
        return topicData.topic?.add_point_customization?.suppress_confetti === true;
    }

    private removeTemporaryIcons() {
        if (this.temporaryIcon) {
            updateMarkerVisibility(this.temporaryIcon, false);
            setMarkerMap(this.temporaryIcon, null);
            this.temporaryIcon = undefined;
        }
        if (this.temporaryCircle) {
            updateMarkerVisibility(this.temporaryCircle, false);
            setMarkerMap(this.temporaryCircle, null);
            this.temporaryCircle = undefined;
        }
    }

    private resetLocationValue () {
        if (isDefined(this.locationElement)) {
            this.userInputtedLatLng = undefined;
            this.locationElement.value = undefined;
        }
        // dispatch event to reset userSelectedLatLng
        topicData.mapComponent?.resetUserSelectedLatLng();
        drawingTool.reset();
        this.customShape = undefined;
    }


    private refreshEmail() {
        // If the email element is hidden and the user is not the owner or writer, return without refreshing.
        if (topicData.topic?.add_point_customization?.email?.hide === true && !topicData.isOwner && !this.isWriter) {
            return;
        }

        // If the email element does not exist, return without refreshing.
        if (!this.emailElement) {
            return;
        }

        // If the email element does not have a value, set it to the cached email or the topic owner's email.
        if (!this.emailElement.value && !this.dontSaveEmailAfterSubmit) {
            const cachedEmail = getFromLocalStorage('email');
            if (this.isWriter) {
                this.emailElement.value = undefinedIfEmpty(accountInfo.email) ?? getFromLocalStorage('email') ?? topicData.email;
            } else if (cachedEmail) {
                this.emailElement.value = cachedEmail;
            }
        }

        if (this.dontSaveEmailAfterSubmit) {
            this.emailElement.value = '';
        }
    }

    private removeOption(id: string | undefined, value: string) {
        const options = this.selectedOptions.get(id ?? '') ?? [];
        const index = options.indexOf(value);
        if (index !== -1) {
            options.splice(index, 1);
            this.selectedOptions.set(id ?? '', options);
            this.requestUpdate();
        }
    }

    private turnOnDescriptionPreviewMode() {
        const turndownService = new TurndownService();
        const description = turndownService.turndown(this.descriptionElement?.root?.innerHTML ?? '');
        this.previewDescriptionText = new DOMParser()
            .parseFromString(description, 'text/html')
            .documentElement.textContent ?? '';
        this.descriptionPreviewMode = true;
    }

    private onUploadCustom(e: CustomEvent) {
        if (e.detail?.imageUrl === undefined) {
            return;
        }
        this.custom_icon_url = e.detail?.imageUrl;
    }

    private async getTimeZoneForLocation() {
        const location = this.locationElement.value;
        if (!isDefined(location) || !isDefined(location.latLng)) {
            return;
        }
        const timezoneInfos = await timezoneAsync(location.latLng.lat, location.latLng.lng);
        this.timezoneShortName = timezoneInfos[0]?.shortIdentifier;
        this.timezoneId = timezoneInfos[0]?.identifier;
        // Refresh the preview now that we know the row's actual zone —
        // before the lookup it was using the browser fallback.
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.refreshTimeRangePreview();
    }

    /**
     * Recompute the "how it'll appear on the point card" preview from
     * the current picker values. Reuses `getDateTimeStringForPoint` —
     * the same helper that renders the actual point card — so the
     * preview is guaranteed to match what the submitter will see after
     * save. Tolerant of incomplete picker state (no start, missing
     * location, tz lookup in flight) — it just clears the preview.
     */
    private async refreshTimeRangePreview() {
        try {
            if (topicData.topic === undefined) return;
            if (!topicData.topic.has_dates) return;
            const location = this.locationElement?.value;
            if (!isDefined(location) || !isDefined(location.latLng)) {
                this.timeRangePreview = undefined;
                return;
            }
            // Reuse the save path's UTC computation so the preview
            // exactly matches what gets persisted. processDateTimeInputs
            // can throw on invalid input — bail quietly in that case
            // rather than fire an error toast on every keystroke.
            let start: string | undefined;
            let end: string | undefined;
            try {
                ({ start, end } = await this.processDateTimeInputs());
            } catch {
                this.timeRangePreview = undefined;
                return;
            }
            if (start === undefined) {
                this.timeRangePreview = undefined;
                return;
            }
            // Build a minimal point shape — only the fields
            // getDateTimeStringForPoint reads are required. The cast
            // through `Partial` lets us pass an object that may not
            // have every required GetPointResponse field while still
            // satisfying the helper, which only reads location/start/end.
            const previewPoint = {
                ...(this.point ?? {}),
                location,
                start,
                end,
            } as unknown as GetPointResponse;
            this.timeRangePreview = await getDateTimeStringForPoint(
                previewPoint,
                topicData.topic,
            );
        } catch {
            this.timeRangePreview = undefined;
        }
    }

    private listingFieldPlaceholder(fieldId?: string): string | undefined {
        const value = fieldId ? this.listingFieldValues.get(fieldId) : undefined;
        return value ? `From listing: ${value}` : undefined;
    }

    // The point's own stored value for a field — inherited (`from_listing`)
    // entries stay out of the inputs so they keep showing as placeholders and
    // never save back as point overrides.
    private pointOwnFieldValue(fieldId?: string): string | undefined {
        return this.point?.custom_information?.custom_field_entries?.find(
            (entry) => entry.from_listing !== true && entry.field_id.$oid === fieldId
        )?.field_value;
    }

    private turnOffDescriptionPreviewMode() {
        this.descriptionPreviewMode = false;
        this.previewDescriptionText = '';
    }

    get previewDescription() {
        return unsafeHTML(parseDescriptionText(this.previewDescriptionText ?? '', false, topicData.topic?.embed_all_description_links));
    }

    private async pointChanged() {
        if (this.point === undefined) {
            this.existingImages = [];
            this.selectedTags = [];
            if (this.nomination_category_id) {
                this.topicCategoryId = this.nomination_category_id;
            } else if (topicData.categories.length === 1) {
                this.topicCategoryId = topicData.categories[0]._id?.$oid;
            } else {
                this.topicCategoryId = getFromLocalStorage(`${topicData.topicId}_last_category`) ?? undefined;
            }
            this.customShape = undefined;
            this.custom_icon_url = undefined;
            this.listingFieldValues = new Map();
            return;
        }

        this.listingFieldValues = new Map();
        const listingOrganizationId = topicData.organizationId;
        if (this.point.directory_entry_id?.$oid && listingOrganizationId) {
            getPointListingSync(AddEditPointComponent.amplitude_callsite, listingOrganizationId, this.point._id.$oid)
                .then((sync) => {
                    this.listingFieldValues = new Map(
                        (sync.listing_field_entries ?? [])
                            .map((entry): [string, string] => [entry.field_id.$oid ?? '', entry.field_value ?? entry.field_values?.join(', ') ?? ''])
                            .filter(([id, value]) => id !== '' && value !== '')
                    );
                })
                .catch(() => { /* collaborators without org access just don't get the hints */ });
        }

        // Prefer the URL's point_key (per-point unique edit link) so this works on a now-private
        // topic where topicData.topicKey is empty; fall back to topicKey for legacy callers.
        const imagesKey = (getQueryStringParameters().get('point_key') ?? undefined) ?? topicData.topicKey;
        this.existingImages = await getPointImages(
            AddEditPointComponent.amplitude_callsite,
            topicData.topicId,
            this.point._id.$oid,
            imagesKey
        );

        if (
            topicData.topicId === undefined ||
            this.point === undefined
        ) {
            getMainPage().errorToastElement?.showToastAndThrow(
                'The `point` was set, but the `topic_id`, `topic_key`, `topic`, `point_id`, or `point_key` was not set.'
            );
        }

        // Get the topic and block.
        await getMainPage().errorToastElement?.tryOrErrorToast(async () => {
            if (this.point === undefined) {
                return;
            }

            if (this.emailElement) this.emailElement.value = this.point.email ?? '';
            if (isDefined(this.locationElement)) this.locationElement.value = this.point.location ?? undefined;
            if (this.numberElement) this.numberElement.value = this.point.number ?? '';
            this.selectedTags = [...new Set(this.point?.tag_info?.user_tags.map(tag => tag._id))];
            this.topicCategoryId = this.point.topic_category_id.$oid;
            if (this.nameElement) this.nameElement.value = this.point.name ?? '';
            if (this.urlElement) this.urlElement.value = this.point.url ?? '';
            if (this.descriptionElement?.root) this.descriptionElement.root.innerHTML = marked.parse(this.point.description ?? '');
            this.customShape = pointCache.getShapeHolderForPoint(this.point) ?? this.userDrawnShapeGeometry;
            this.custom_icon_url = this.point.custom_icon_url;

            this.selectedOptions = new Map();
            if (this.point.custom_information) {
                for (const customField of topicData.topic?.add_point_customization?.custom_fields ?? []) {
                    if (customField._id === undefined || customField.customizations.hide === true || customField.archived) {
                        continue;
                    }
                    if ('MultiSelect' in customField.field_type && customField._id.$oid !== undefined && this.point.custom_information.custom_field_entries) {
                        const matchingEntries = this.point.custom_information.custom_field_entries.filter(entry => entry.from_listing !== true && entry.field_id.$oid === customField._id?.$oid);
                        const fieldValues = matchingEntries.map(entry => entry.field_value?.split(',') ?? []);
                        const flatFieldValues = fieldValues.flat();
                        this.selectedOptions.set(customField._id.$oid ?? '', flatFieldValues);
                    }
                }
            }

            if (topicData.topic?.has_dates === true && isDefined(this.dateTimeRangePickerElement) && this.locationElement.value?.latLng) {
                const timezoneInfos = await timezoneAsync(
                    this.locationElement.value.latLng.lat,
                    this.locationElement.value.latLng.lng
                );
                const timeZoneId = timezoneInfos[0]?.identifier;
                this.timezoneShortName = timezoneInfos[0]?.shortIdentifier;
                this.timezoneId = timeZoneId;
                const startUTC = convertUTCToLocale(this.point.start as string, timeZoneId);
                const endUTC = convertUTCToLocale(this.point.end as string, timeZoneId);
                if (isDefined(this.dateTimeRangePickerElement.startDateElement)) {
                    (this.dateTimeRangePickerElement.startDateElement.value as any) = this.point.start
                        ? format(startUTC, 'yyyy-MM-dd')
                        : '';
                };

                // if there is no end option, set to undefined so it doesnt cause validaiton issues down the line
                if (topicData.topic?.has_end_date_time === false) {
                    this.point.end = undefined;
                }
                if (isDefined(this.dateTimeRangePickerElement.endDateElement)) {
                    (this.dateTimeRangePickerElement.endDateElement.value as any) = this.point.end
                        ? format(endUTC, 'yyyy-MM-dd')
                        : '';
                }

                const startTimeString = this.point.start ? format(startUTC, 'HH:mm') : '';
                const endTimeString = this.point.end ? format(endUTC, 'HH:mm') : '';

                this.dateTimeRangePickerElement.isIncludingEndDateTime = this.point.end !== undefined && topicData.topic?.has_end_date_time !== false && this.point.start !== this.point.end;
                this.dateTimeRangePickerElement.allDay = startTimeString === '00:00' && endTimeString === '23:59' && topicData.topic?.has_time_only !== true;

                if (startTimeString !== '00:00' && isDefined(this.dateTimeRangePickerElement.startTimeElement)) {
                    (this.dateTimeRangePickerElement.startTimeElement.value as any) =
                        startTimeString;
                }
                if (endTimeString !== '23:59' && isDefined(this.dateTimeRangePickerElement.endTimeElement)) {
                    (this.dateTimeRangePickerElement.endTimeElement.value as any) = endTimeString;
                }
            }

            // check for excpetions
            // for welcomehome, we hide the custom fields if the category name does not contain "Rental Property"
            if (topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations) {
                const category = topicData.categories.find((c) => c._id?.$oid === this.topicCategoryId);
                if (category?.name?.toLowerCase().includes('rental property') === true) {
                    this.exceptionHideCustomFields = false;
                } else {
                    this.exceptionHideCustomFields = true;
                }
            }
        }, '');
    }

    get currentValues() {
        // these must exactly match the setting names on admin-collab-settings-addon
        // TODO: Make this a type? add undefined if empty
        const currentValues: { [key: string]: string | undefined } = {
            category: undefinedIfEmpty(this.topicCategoryId),
            number: undefinedIfEmpty(this.numberElement?.value),
            title: undefinedIfEmpty(this.nameElement?.value),
            more_info: undefinedIfEmpty(this.urlElement?.value ?? ''),
            description: undefinedIfEmpty(this.descriptionElement?.root?.innerHTML ?? ''),
            special_offer: undefinedIfEmpty(this.specialOfferDescriptionElement?.value ?? ''),
            tags: undefinedIfEmpty(this.selectedTags.map(tag => tag.$oid).join(','))
        };

        // add custom fields to current values
        for (const customField of topicData.topic?.add_point_customization?.custom_fields ?? []) {
            if (customField._id === undefined || customField.customizations.hide === true) {
                continue;
            }
            if ('MultiSelect' in customField.field_type && customField._id?.$oid !== undefined) {
                const selectedOptions = this.selectedOptions.get(customField._id.$oid) ?? [];
                // push selected options to current values
                currentValues[customField._id.$oid] = undefinedIfEmpty(selectedOptions.join(','));
            } else {
                const inputElement = this.shadowRoot?.querySelector(`#id${customField._id.$oid}`) as HTMLInputElement;
                if (inputElement === null || customField._id.$oid === undefined) {
                    continue;
                }
                currentValues[customField._id.$oid] = undefinedIfEmpty(inputElement.value);
            }
        }

        return currentValues;
    }

    async getFormProducts() {
        this.formProducts = await getOrganizationProductsPublic(PaymentTargetType.Form, topicData.topicId, topicData.topic?.organization_id?.$oid ?? '');
    }

    get visibleProductIds(): string[] {
        const visibleProductIds: string[] = [];
        for (const product of this.formProducts) {
            if (product.status !== 'active') {
                continue;
            }
            const visibilityRule = this.getVisibilityRuleForProduct(product);

            if (checkProductVisibility(visibilityRule, this.currentValues)) {
                visibleProductIds.push(product._id.$oid);
            }
        }
        return visibleProductIds;
    }

    private getVisibilityRuleForProduct(product: GetPublicOrganizationProductResponse) {
        if ('Form' in product.item_details) {
            return product.item_details.Form.visibility_rules;
        }
        return undefined;
    }

    throwValidationError(message: string) {
        this.validationInProgress = false;
        getMainPage().errorToastElement?.showToastAndThrow(message);
    }

    private async save() {
        // Validation
        if (!this.categorySelectorElement ? false : !this.categorySelectorElement?.reportValidity()) {

            this.throwValidationError(
                'Please fix the mandatory category selection field.'
            );
        }

        if (!isDefined(this.locationElement.value)) {
            this.throwValidationError(
                'Please fix the mandatory location field.'
            );
        }

        if (!pointNumberValid(this.numberElement?.value?.trim())) {
            this.throwValidationError('Assigned number must be up to 8 letters, numbers, or hyphens.');
        }

        if (!this.nameElement ? false : !this.nameElement?.reportValidity()) {
            if (topicData.topic?.add_point_customization?.title?.mandatory === true) {
                this.throwValidationError(
                    'Please fix the mandatory title input field.'
                );
            } else {
                this.throwValidationError(
                    'Please fix the invalid title field.'
                );
            }
        }

        if (!this.descriptionElement ? false : !this.descriptionElement.root) {
            this.throwValidationError(
                'Please fix the invalid Description field.'
            );
        }
        if (topicData.topic?.add_point_customization?.description?.mandatory === true && this.descriptionElement?.root?.innerHTML.length === 0) {
            this.throwValidationError(
                'Description is mandatory. Please add additional description.'
            );
        }

        if (this.descriptionElement?.root && this.descriptionElement.getInnerCharCount() > 6000) {
            this.throwValidationError(
                `Description is too long ${this.descriptionElement.getInnerCharCount()} chars. Please shorten the description to 6000 characters or less.`
            );
        }

        if (topicData.topic?.add_point_customization?.tags?.mandatory === true
            && checkTagsFieldVisibility(topicData.topic, this.currentValues)
            && this.selectedTags.length === 0) {
            this.throwValidationError(
                'Tags are mandatory. Please select at least one tag.'
            );
        }

        if (!this.urlElement ? false : !this.urlElement?.reportValidity()) {
            if (topicData.topic?.add_point_customization?.more_info?.mandatory === true) {
                this.throwValidationError(
                    'Please fix the mandatory More Info input field.'
                );
            } else {
               this.throwValidationError(
                    'Please fix the invalid More Info field.'
                );
            }
        }

        // if there is not a special offer description elent, skip
        if (this.specialOfferDescriptionElement && (this.hasSpecialOffer ?? false) && !this.specialOfferDescriptionElement?.value?.trim()) {
            this.throwValidationError(`Please input description for ${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'}.`);
        }
        if (topicData.topic?.add_point_customization?.special_offer?.hide === false
            && topicData.topic?.add_point_customization?.special_offer?.mandatory === true
            && !this.specialOfferDescriptionElement?.value?.trim()) {
            this.throwValidationError(
                `Please fix the mandatory ${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'} input field.`
            );
        }

        // get the shape if there is one so we can remove or update it later
        const shape = this.customShape;
        if (shape && this.showShapeEditor) {
            // removes editables and replaces with the shape
            await this.toggleShapeEditor();
        }

        // Handle special case
        // if topidException is WelcomeHome and tag "visited" is selected, custom fields are required
        const visited_tag = topicData.allUserTags.find(category => category.name === 'Visited');
        const exceptionCustomFieldsRequired = topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations && this.selectedTags.some(tag => tag.$oid === visited_tag?._id?.$oid);

        // validate custom fields, if they exist
        if (topicData.topic?.add_point_customization?.custom_fields) {
            for (const customField of topicData.topic.add_point_customization.custom_fields) {

                let is_required = customField.customizations.mandatory === true;

                // random need for an integraiton customer (Welcome Home) to require some fields
                // in the future we could make logic based on the categories or tags that are selected
                if (exceptionCustomFieldsRequired) {
                    if (customField?.customizations?.header) {
                        is_required = !customField.customizations.header.includes('Neighborhood');
                    } else {
                        is_required = true;
                    }
                }

                // Check if the field type and ID are defined, and the field is actually rendered
                if (customField.field_type !== undefined && customField._id !== undefined && this.isCustomFieldRendered(customField)) {
                    if ('MultiSelect' in customField.field_type) {
                        // For 'MultiSelect' type fields, check the selected options map
                        const selectedOptions = this.selectedOptions.get(customField._id.$oid);
                        // Check if the field is mandatory and empty
                        if (is_required && (!selectedOptions || selectedOptions.length === 0)) {

                            this.throwValidationError(
                                `The ${customField.customizations.header ?? 'custom'} field is mandatory. Please select at least one option.`
                            );
                        }
                    } else {
                        // For other types of fields, check the input element
                        const inputElement = this.shadowRoot?.querySelector(`#id${customField._id?.$oid}`) as HTMLInputElement;
                        if (!isDefined(inputElement)) {
                            continue;
                        }
                        // Check if the field is mandatory and empty
                        if ((inputElement.value === undefined || inputElement?.value?.trim() === '') && is_required) {

                            this.throwValidationError(
                                `The ${customField.customizations.header ?? 'custom'} field is mandatory. Please provide a value.`
                            );
                        }
                        // Check the validity of the input field
                        else if (!inputElement.reportValidity()) {
                            this.throwValidationError(
                                `The value for the ${customField.customizations.header ?? 'custom'} field is invalid. Please fix it.`
                            );
                        }
                    }
                }
            }
        }

        const email = undefinedIfEmpty(this.emailElement?.value?.trim().toLowerCase()) ?? undefinedIfEmpty(accountInfo.email) ?? getFromLocalStorage('email') ?? undefined;

        if (email) {
            this.refreshEmail();
        }

        // If email is hidden or user is owner or writer, skip. then if email is inputted or required, check validity
        if (topicData.topic?.add_point_customization?.email?.hide !== true && !topicData.isOwner && !this.isWriter) {
            if (isDefined(email) || topicData.topic?.add_point_customization?.email?.mandatory !== false) {
                const emailValid = email === undefined ? false : validateEmail(email);
                if (!emailValid) {
                    this.throwValidationError('Invalid email format.');
                }
            }

        }
        if (!this.isWriter && isValidString(topicData.topic?.add_point_customization?.terms?.header) && !this.termsAndConditionsChecked) {
            this.throwValidationError(`Please check ${topicData.topic?.add_point_customization?.terms?.header ?? 'the terms and conditions'} at the bottom of the form.`);
        }

        // Save the email if we are not in edit mode.
        if (!this.editMode && !accountInfo.isAuthenticated && email !== undefined) {
            saveEmail(email);
        }

        // Save if we are in point page and email is not undefined and dontSaveEmailAfterSubmit is false
        if (this.isPointPage && !this.dontSaveEmailAfterSubmit && email !== undefined) {
            saveEmail(email);
        }

        let start = this.point?.start;
        let end = this.point?.end;
        if ((topicData.topic?.has_dates) ?? false) {
            const startValid = this.dateTimeRangePickerElement?.startDateElement?.validate() ?? true;

            const endValid = this.dateTimeRangePickerElement?.endDateElement?.validate() ?? true;
            const valid = startValid && endValid;

            if (!valid) {
                this.throwValidationError('Please fix your date inputs.');
            }

            ({ start, end } = await this.processDateTimeInputs());

            // Check if date/time is mandatory and validate
            if (topicData.topic?.add_point_customization?.date_time?.mandatory === true
                && checkDateTimeFieldVisibility(topicData.topic, this.currentValues)) {
                if (start === undefined || start === '') {
                    this.throwValidationError('Date/Time is required.');
                }
            }

            // handle exception for WelcomeHomeRelocations
            if (exceptionCustomFieldsRequired) {
                // if point start is undefined or empty, throw error
                if (start === undefined || start === '') {
                    this.throwValidationError('Visit Date is Required for Visited Locations.');
                }
            }


            if (
                start !== undefined &&
                end !== undefined &&
                new Date(start).getTime() > new Date(end).getTime()
            ) {
                this.throwValidationError('Please fix your invalid inputs (the start date occurs after the end date).');
            }
        }

        // A map with no categories has nothing to pick, so send the
        // no-category id rather than asking for a selection that can't
        // be made. Otherwise the choice must match one the map offers.
        const categoryIds = topicData.categories.map((category) => category._id?.$oid);
        if (!categoryIds.includes(this.topicCategoryId)) {
            this.topicCategoryId = topicData.categories.length === 0 ? ZERO_OBJECT_ID : undefined;
        }

        if (this.topicCategoryId === undefined) {
            this.throwValidationError('Please fix your category selection.');
            throw new Error('topicCategoryId is undefined');
        }

        if (this.locationElement.value === undefined) {
            this.throwValidationError('Please fix your location input.');
            throw new Error('location is undefined');
        }

        const topic_category_id: ObjectId = { $oid: this.topicCategoryId };
        // Geocode Location Result only set after successful geocoding, so we can set this as the default case and fallback to the locationElement.
        const location: CreateLocationRequest = this.locationElement.value;
        const number = undefinedIfEmpty(this.numberElement?.value?.trim());

        // NOTE: This is a bit tricky: we want this to be undefined if it is undefined or '', so that it will not
        // even get serialized to the database.
        const name = undefinedIfEmpty(this.nameElement?.value?.trim());
        const url = undefinedIfEmpty(this.urlElement?.value?.trim());

        const turndownService = new TurndownService();
        let description = turndownService.turndown(this.descriptionElement?.root?.innerHTML ?? '');
        description = new DOMParser()
            .parseFromString(description, 'text/html')
            .documentElement.textContent ?? '';

        const specialOfferDescription = this.specialOfferDescriptionElement?.value ?? '';
        const specialOfferContent = (this.hasSpecialOffer ?? false)
            ? {
                  description: specialOfferDescription,
              }
            : undefined;

        const tag_info: AddTagRequest = {
            user_tags: this.selectedTags.map(tag => ({ _id: tag })),
        };

        const custom_information: AdditionalPointDetails = {
            custom_field_entries: this.getCustomFormEntries(),
            passthrough_parameters: this.getPassthroughParams(),
        };

        const custom_icon_url = undefinedIfEmpty(this.custom_icon_url);


        let isGateHidden = false;
        if (topicData.isWriter && topicData.hasActiveGate) {
            // hide by default if there is a gate
            isGateHidden = true;
        }

        const createPointRequest: CreatePointRequest = {
            _id: isDefined(this.point) ? this.point._id : undefined,
            email,
            topic_id: { $oid: topicData.topicId },
            topic_category_id,
            location,
            name,
            custom_shape: this.customShape?.shape,
            url,
            description,
            special_offer: specialOfferContent,
            start,
            end,
            number,
            source: undefined,
            custom_icon_url,
            tag_info,
            // These should only be set by topic admins from the command center, so no need to set them here.
            // It is an error for a non-admin to set these.
            active: undefined,
            featured: undefined,
            custom_information,
            is_gate_hidden: isGateHidden,
            embedded_video: undefined,
            url_images: undefined,
            is_nomination: this.isNominationPage,
        };

        this.validationInProgress = false;
        // Proceed with point creation (either editing or no payment required)
        await this.createPoint(createPointRequest, shape);
    }

    private async createPoint(createPointRequest: CreatePointRequest, shape?: TopLevelShapeHolder) {
        // Set html to upload
        this.uploadInProgress = true;

        // later add check for writer, already paid, edit etc.
        const paymentRequired = this.visibleProductIds.length > 0 && !this.isWriter && isDefined(this.preCheckoutElement) && this.preCheckoutElement.remainingLineItems.length > 0;

        if (paymentRequired && isDefined(this.preCheckoutElement)) {
            if (createPointRequest.email === undefined) {
                createPointRequest.email = prompt('Email is required to purchase products. Please enter your email.') ?? undefined;
                if (createPointRequest.email === undefined) {
                    this.throwValidationError('Email is required for payment.');
                    return;
                }
            }

            let edit_key;
            if (this.isPointPage && !topicData.isWriter) {
                const queryString = getQueryStringParameters();
                const modificationKey = queryString.get('point_key');
                if (modificationKey) {
                    edit_key = modificationKey;
                }
            }
            const response = await startStripeCheckoutSession(createPointRequest.email, this.preCheckoutElement.remainingLineItems, topicData.organizationId ?? '', topicData.topicId, PaymentTargetType.Form, createPointRequest, this.point?._id.$oid, topicData.topicKey, edit_key);

            // open url in same tab
            if (isDefined(response) && isDefined(response.url)) {
                window.location.href = response.url;
            }
            return;
        }

        try {
            if (this.editMode) {
                const pointId: string = this.point?._id.$oid ?? '';
                await pointCache.modifyAndFetch(pointId, createPointRequest, true, this.uploadedImages, this.imagesToDelete, undefined, !this.isPointPage);
            } else {
                try {
                    await pointCache.fetchAndAdd(createPointRequest, this.uploadedImages, this.imagesToDelete);
                } catch (e) {
                    this.uploadInProgress = false;
                    console.error('An error has occurred.  Context: the server rejected the request due to validation issues. Try refreshing the page and trying again.');
                    return;
                }
            }
        } catch (_) {
            this.uploadInProgress = false;
            this.throwValidationError('The server rejected the request due to validation issues. Try refreshing the page and trying again.');
        }
        if (!this.editMode) {
            removeShapeFromMap(shape?.inner);
        }
        await this.runAfterCreateOrEditActions();
    }

    private async runAfterCreateOrEditActions() {
        // Removes all elements.
        const currentTopicId = topicData.topicId;
        if (currentTopicId && this.topicCategoryId) {
            setLocalStorage(`${currentTopicId}_last_category`, this.topicCategoryId);
        }
        this.uploadInProgress = false;
        this.uploadedImages = [];
        this.imagesToDelete = [];
        this.resetLocationValue();
        this.selectedTags = [];
        this.selectedOptions = new Map();
        this.custom_icon_url = undefined;

        this.customShape = undefined;
        this.success = true;
        if (!topicData.isWriter && !this.isConfettiSuppressed) {
            show_confetti([topicData.backgroundColor, topicData.textColor]);
        }

        // close drawing lib

        if (isDefined(topicData.postPointSubmissionPopUp) && !topicData.isWriter) {
            await this.hardReset();
            await topicData.mapComponent?.openPostSubmissionPopUp();
        } else if (this.editMode || this.isWriter) {
            setTimeout(async () => {
                await this.hardReset();
            }, 2000);
        }
    }

    private async delete() {
        await getMainPage().errorToastElement?.tryOrErrorToast(async () => {
            if (isDefined(this.point)) {
                await pointCache.remove(this.point?._id.$oid);
            }
        }, 'An error has occurred.  Context: the server rejected the request due to validation issues. Try refreshing the page and trying again.');


        this.dispatchEvent(new CustomEvent('close-point-modal', {
            bubbles: true,
        }));

        this.dispatchEvent(new CustomEvent('on-delete-point')); // this is for point page to know that the point has been deleted

        logWithAmplitude(AddEditPointComponent.amplitude_callsite, 'Delete Click', {
            point_id: this.point?._id.$oid,
        });

    }

    private termsAndConditionsSetting() {
        this.termsAndConditionsChecked = !this.termsAndConditionsChecked;
    }

    async hardReset() {
        // Adds all elements back.
        this.success = true;
        this.requestUpdate();
        this.dispatchEvent(new CustomEvent('close-point-modal', { // when close-point-modal is called, this thing should reset itself
                detail: {
                    message: 'Point Added.',
                },
            }));

        // Give it a sec to close so people dont see the full form pop up again
        await delay(1000);
        if (topicData.topic?.requires_approval !== true && featuresInfo.pointsPerMapEnabled > pointCache.allPoints.length) {
            this.removeTemporaryIcons();
        }

        this.success = false;

        // Force an update and wait for it to complete.
        await this.requestUpdate();

        // Rerun "startup" tasks.
        await this.startup();

        // Call "pointChanged" to simulate a point being bound (if there is none, then the method exits).
        await this.pointChanged();
    }

    async addAnother() {
        this.customShape = undefined;
        await topicData.mapComponent?.setMapDrawer(MapDrawer.AddPoint);

        if (this.isPointPage) {
            await this.pointChanged();
            this.success = false;
        }
    }

    private getPassthroughParams(): PointPassthroughParameter[] {
        const urlParams = new URLSearchParams(window.location.search);
        let passthroughParams: PointPassthroughParameter[] | undefined = this.point?.custom_information?.passthrough_parameters;

        try {
            urlParams.forEach((value, key) => {
                if (key !== 'topic_key' && key !== 'topic_id' && key !== 'point_id' && key !== 'point_key') {
                    if (!passthroughParams) {
                        passthroughParams = [];
                    }

                    const param = passthroughParams.find(param => param.key === key);
                    if (param) {
                        param.value = value;
                    } else {
                        passthroughParams.push({ key, value });
                    }
                }
            });
        } catch (error) {
            console.error('An error occurred while processing URL parameters:', error);
        }

        return passthroughParams ?? [];
    }

    private async processDateTimeInputs() {
        const startValid = this.dateTimeRangePickerElement?.startDateElement?.validate() ?? true;
        const endValid = this.dateTimeRangePickerElement?.endDateElement?.validate() ?? true;
        const locationValid = this.locationElement?.value !== undefined;
        const valid = startValid && endValid && locationValid;
        const isAllDay = this.dateTimeRangePickerElement?.allDay;
        const timeOnly = topicData.topic?.has_time_only === true;

        if (!valid) {
            getMainPage().errorToastElement?.showToastAndThrow(
                'Please fix your invalid inputs (marked red).'
            );
        }

        const isNotAllDay = !(isAllDay === true) || timeOnly;
        let startDate = this.dateTimeRangePickerElement?.startDateElement?.value;

        if (startDate === undefined) {
            console.error('start date is undefined');
            return { start: undefined, end: undefined };
        }

        let endDate = this.dateTimeRangePickerElement?.endDateElement?.value ?? startDate;
        const startValue = undefinedIfEmpty(this.dateTimeRangePickerElement?.startTimeElement?.value);
        const startTime = startValue ?? '00:00';
        const endValue = undefinedIfEmpty(this.dateTimeRangePickerElement?.endTimeElement?.value);
        const endTime = endValue ?? '23:59';

        // if time only, set the start date and end date to the dummy date
        if (timeOnly) {
            startDate = DUMMY_DATE;
            endDate = DUMMY_DATE;
        }

        const startDateTime = isNotAllDay
            ? `${startDate}T${startTime}:00Z`
            : `${startDate}T00:00:00Z`;

        const endDateTime = isNotAllDay
            ? `${endDate}T${endTime}:00Z`
            : `${endDate}T23:59:00Z`;

        const autoEndDateTIme = this.dateTimeRangePickerElement?.startDateElement?.value
            ? isNotAllDay
                ? `${startDate}T${startTime}:00Z`
                : `${startDate}T23:59:00Z`
            : `${DUMMY_DATE}T${startTime}:00Z`;

        const timezoneInfos = await timezoneAsync(
            this.locationElement.value?.latLng?.lat ?? 0,
            this.locationElement.value?.latLng?.lng ?? 0
        );
        const timeZoneId = timezoneInfos[0]?.identifier;

        const startPresent = isDefined(undefinedIfEmpty(this.dateTimeRangePickerElement?.startDateElement?.value)) || (timeOnly && isDefined(undefinedIfEmpty(this.dateTimeRangePickerElement?.startTimeElement?.value)));
        const endPresent = isDefined(undefinedIfEmpty(this.dateTimeRangePickerElement?.endDateElement?.value)) || (timeOnly && this.dateTimeRangePickerElement?.isIncludingEndDateTime === true && isDefined(undefinedIfEmpty(this.dateTimeRangePickerElement?.endTimeElement?.value)));
        const includingEndDateTime = this.dateTimeRangePickerElement?.isIncludingEndDateTime;

        const start = startPresent ? convertLocaleToUTC(startDateTime, timeZoneId)?.toISOString() : undefined;
        let end = endPresent ? convertLocaleToUTC(endDateTime, timeZoneId)?.toISOString() : undefined;

        if (!endPresent && includingEndDateTime === false && start !== undefined) {
            end = convertLocaleToUTC(autoEndDateTIme, timeZoneId)?.toISOString();
        }

        return { start, end };
    }

    private getCustomFormEntries(): PointFieldEntry[] {
        // Inherited listing values are read-time only — a save payload must
        // carry just the point's own entries (typing a value creates one).
        const custom_form_entries = (this.point?.custom_information?.custom_field_entries ?? []).filter(entry => entry.from_listing !== true);
        for (const customField of topicData.topic?.add_point_customization?.custom_fields ?? []) {
            if (customField._id === undefined || !this.isCustomFieldRendered(customField)) {
                continue;
            }
            let fieldValue: string | string[] | undefined;
            if ('MultiSelect' in customField.field_type) {
                fieldValue = this.selectedOptions.get(customField._id.$oid ?? '')?.join(',');
            } else {
                const customFieldElement = this.shadowRoot?.querySelector(`#id${customField._id.$oid}`) as HTMLInputElement;
                if (customFieldElement === null) {
                    continue;
                }
                fieldValue = undefinedIfEmpty(customFieldElement.value);
            }
            const existingField = custom_form_entries.find(field => field.field_id.$oid === customField._id?.$oid);
            if (existingField) {
                existingField.field_value = fieldValue;
            } else if (fieldValue !== '' && fieldValue !== undefined && fieldValue.length !== 0) {
                custom_form_entries.push({
                    field_id: customField._id,
                    field_value: fieldValue,
                });
            }
        }
        return custom_form_entries;
    }


    private setUserSelectedLatLng(l: Location) {
        if (isDefined(this.locationElement)) {
            this.locationElement.value = l;
        }
    }

    private setuserDrawnShapeGeometry(holder: TopLevelShapeHolder) {
        const shape = holder.shape;
        shape._id = this.point?._id;
        shape.name = this.point?.name;

        this.customShape = holder;
        const to_be_centered = {
            type: GeoJsonType[shape.geojson.type as keyof typeof GeoJsonType],
            coordinates: shape.geojson.coordinates,
        };
        if (to_be_centered.coordinates !== undefined && shape.geojson.type !== 'GeometryCollection') {
            const refined_geometry = geometry(shape.geojson.type, to_be_centered.coordinates);
            const polygon = center(refined_geometry);

            const newLocation: Location = {
                search: 'Shape',
                place_id: 'shape',
                latLng: {
                    lat: polygon.geometry.coordinates[1] ?? 0,
                    lng: polygon.geometry.coordinates[0] ?? 0
                }
            };
            this.locationElement.value = newLocation;
        } else if (shape.geojson.type === 'GeometryCollection') {
            this.locationElement.value = {
                search: 'Shape',
                place_id: 'shape',
                latLng: {
                    lat: topicData.mapComponent?.googleMapComponent?.map?.getCenter().lat() ?? 0,
                    lng: topicData.mapComponent?.googleMapComponent?.map?.getCenter().lng() ?? 0
                }
            };
        }
    }


    private async locationChanged() {
        // prevent infinite loop
        if (this.previousLocationValue === this.locationElement.value) {
            return;
        }
        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (this.locationElement.value?.search && this.locationElement.value.search !== '') {
            const locationString: string = this.locationElement.value.search;
            const splits = locationString.split(',');
            if (splits.length === 5 && splits[0].trim().length <= 50 && this.nameElement !== null && this.nameElement !== undefined) {
                this.nameElement.value = splits[0].trim();
            }
        }

        // look for lat long structure
        if (this.locationElement.value?.search && this.locationElement.value.search.length > 3 && this.userSelectedLatLng === undefined) {
            const isLatLong = await isLocationInputLatLong(this.locationElement.value.search);

            if (isLatLong !== false) {
                const latLongInput : Location = {
                    search: this.locationElement.value?.search ?? '',
                    place_id: 'custom',
                    latLng: isLatLong,
                };

                this.locationElement.value = latLongInput;

                this.previousLocationValue = {
                    search: this.locationElement.value.search ?? '',
                    place_id: this.locationElement.value.place_id ?? '',
                    latLng: this.locationElement.value.latLng ?? { lat: 0, lng: 0 },
                };
            }
        }

        if (topicData.topic?.has_dates === true && this.dateTimeRangePickerElement) {
            await this.getTimeZoneForLocation();
        }


        // see if location is already on the map
        if (this.locationElement.value) {
            const existingPoints = pointCache.getPointsWithSameLocation(this.locationElement.value);
            this.isAlreadyOnMap = existingPoints.length > 0;
            this.pointsAlreadyOnMap = existingPoints;
        } else {
            this.isAlreadyOnMap = false;
            this.pointsAlreadyOnMap = [];
        }

        try {
            this.addOrUpdateTemporaryIcons();
        } catch (e) {
            console.error('An error occurred while updating temporary icons:', e);
        }
    }

    private addOrUpdateTemporaryIcons() {
        // if this is a custom shape, return
        if (this.customShape !== undefined) {
            return;
        }
                // show the location on the map with circle around it
        if (this.locationElement.value && topicData.mapComponent && !this.editMode && topicData.map) {
            topicData.mapComponent.googleMapComponent?.map?.panTo(this.locationElement.value.latLng);

            if (this.temporaryIcon) {
                setMarkerMap(this.temporaryIcon, null);
                updateMarkerVisibility(this.temporaryIcon, false);
            }
            if (this.temporaryCircle) {
                setMarkerMap(this.temporaryCircle, null);
                updateMarkerVisibility(this.temporaryCircle, false);
            }
            const iconSize = topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE;
            const icon: google.maps.Icon = {
                url: this.iconPreview ?? 'https://img.icons8.com/?size=80&id=UYZ2A81GqKqU&format=png',
                scaledSize: new google.maps.Size(iconSize * 1.25, iconSize * 1.25),
            };
            if (pointCache.advancedMarkers === false) {
                icon.anchor = new google.maps.Point(iconSize*1.25/2, iconSize*1.25/2);
            };
            const temp_marker = getMarkerElement(topicData.map, this.locationElement.value.latLng, icon, 100, pointCache.advancedMarkers, undefined);
            setMarkerMap(temp_marker, topicData.map);
            updateMarkerVisibility(temp_marker, true);
            this.temporaryIcon = temp_marker;

            const circle_svg = makeColorsIntoNumberSvg(topicData.backgroundColor,'');
            const circle_icon = {
                url: circle_svg,
                scaledSize: new google.maps.Size(iconSize * 2, iconSize * 2),
                anchor: new google.maps.Point(iconSize, iconSize),
            };
            const circle = getMarkerElement(topicData.map, this.locationElement.value.latLng, circle_icon, 90, pointCache.advancedMarkers);
            setMarkerMap(circle, topicData.map);
            updateMarkerVisibility(circle, true);
            this.temporaryCircle = circle;
        }
    }

    private async categorySelected(e: any) {
        if (this.topicCategoryId === e.detail || !isDefined(e.detail)) {
            return;
        }
        this.needsMarkerChange = true;
        this.topicCategoryId = e.detail;

        // check for excpetions
        // for welcomehome, we hide the custom fields if the category name does not contain "Rental Property"
        if (topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations) {
            const category = topicData.categories.find((c) => c._id?.$oid === e.detail);
            if (category?.name?.toLowerCase().includes('rental property') === true) {
                this.exceptionHideCustomFields = false;
            } else {
                this.exceptionHideCustomFields = true;
            }
        }
        this.requestUpdate();
    }

    private async onFileInputChange(event: any) {
        const files = event.target.files;
        for (let i = 0; i < files.length; i++) {
            const file = files.item(i);

            try {
                const base64file = await blobToDataUrl(file);
                const exifData = piexif.load(base64file);
                this.uploadedImages.push({ file, orientation: exifData['0th'][274]} );
                await this.requestUpdate();
            } catch {
                this.uploadedImages.push({ file, orientation: undefined} );
                await this.requestUpdate();
            }

        }
    }

    private async updateDragLocationWithPreviousDetails() {
        if (this.keepPreviousPlaceDetailsCheckboxElement?.checked === true) {
            const newLocation: Location = {
                search: this.locationBeforeDrag?.place_id === 'custom' ? this.locationElement.value?.search ?? '' : this.locationBeforeDrag?.search ?? '',
                place_id: this.locationBeforeDrag?.place_id ?? '',
                latLng: this.locationElement.value?.latLng ?? { lat: 0, lng: 0 },
            };
            // delay one to allow the locationElement to update
            await delay(500);
            this.locationElement.value = newLocation;
        }
    }

    private onAddFilesClick() {
        this.fileInput?.click();
    }

    private async handleClose(image: File) {
        const indexToDelete = this.uploadedImages.findIndex(
            (uploadedFile) => uploadedFile.file.name === image.name
        );
        this.uploadedImages.splice(indexToDelete, 1);
        await this.requestUpdate();
    }

    private async handleExistingDelete(image: Image) {
        const indexToDelete = this.existingImages.findIndex(
            (uploadedFile) => uploadedFile._id === image._id
        );
        this.existingImages.splice(indexToDelete, 1);
        this.imagesToDelete.push(image);
        await this.requestUpdate();
    }

    async handleRotate(image: ImageUpload) {
        if (image == null) {
            return;
        }
        const rotatedFile = await rotateImage(image);
        image = rotatedFile;
        this.rotateValue = rotatedFile.rotate_value ?? 0;
        this.rotatePreviewCSS = `rotate(${this.rotateValue}deg)`;

        await this.requestUpdate();
    }

    private async onDrop(event: any) {
        event.preventDefault();
        for (let i = 0; i < event.dataTransfer.items.length; i++) {
            const file = event.dataTransfer.items[i].getAsFile();
            try {
                const base64file = await blobToDataUrl(file);
                const exifData = piexif.load(base64file);
                this.uploadedImages.push({ file, orientation: exifData['0th'][274]} );
            } catch {
                this.uploadedImages.push({ file, orientation: undefined} );
            }
        }
        await this.requestUpdate();
    }

    private preventDefault(event: any) {
        event.preventDefault();
    }

    private async removeInertOnDropdown() {
        await removeInertFromVaadinOverlay('VAADIN-DATE-PICKER-OVERLAY');
        await removeInertFromVaadinOverlay('VAADIN-TIME-PICKER-OVERLAY');
    }

    private toggleSpecialOffer(e: any) {
        this.hasSpecialOffer = e.detail;
        this.needsMarkerChange = true;
    }

    async addNewTag(e: any) {
        this.selectedTags = [
            ...new Set([...this.selectedTags, e.detail._id]),
        ];

        // toggling this property that gets passed into tags-selector causes it to rerender and update w correct values.
        // mutating array above doesnt. this is stupid but works
        this.rerenderTags = true;
        this.rerenderTags = false;
    }

    async removeTag(e: any) {
        this.selectedTags = this.selectedTags.filter(
            (tag) => tag.$oid !== e.detail._id.$oid
        );

        // toggling this property that gets passed into tags-selector causes it to rerender and update w correct values.
        // mutating array above doesnt. this is stupid but works
        this.rerenderTags = true;
        this.rerenderTags= false;
    }

    async toggleShapeEditor() {
        if (this.showShapeEditor) {
            if (this.shapeStyleEditorElement) {
                if ((countVertices(this.shapeStyleEditorElement?.focusedShape?.shape?.geojson?.coordinates ?? []) < 100 && this.shapeStyleEditorElement?.focusedShape?.shape?.geojson.type !== 'GeometryCollection')) {
                    makeShapeEditable(this.shapeStyleEditorElement.focusedShape?.inner, false);
                }
                this.customShape = this.shapeStyleEditorElement.focusedShape;
            }

            if (this.shapeStyleEditorElement?.circle) {
                this.shapeStyleEditorElement.circle.setVisible(false);
                showShape(this.shapeStyleEditorElement.focusedShape?.inner);
                if (pointCache.map) {
                    addShapeToMap(this.shapeStyleEditorElement.focusedShape?.inner, pointCache.map);
                }
            }
            this.showShapeEditor = false;
        }
        else {
            this.showShapeEditor = true;
        }
    }

    private renderAlreadyOnMapNomination() {
        return html`
            <div class="alreadyOnMapNomination inputSection" style="background-color: #f8d7da; border-color: #f5c6cb;">
                <div style="display: flex; flex-direction:column; padding: 1rem;">
                    <h4>This place is already nominated!</h4>
                    <p>Increase it's chances of inclusion by upvoting it.</p>
                    <div style="display: flex; flex-direction: row; gap: 1rem;">
                        <form-button @click="${() => this.sendToUpvote()}" class="success">Go To Upvote</form-button>
                        <form-button @click="${() => this.addDuplicateAnyway()}" class="success">Add Anyway</form-button>
                    </div>
                </div>
            </div>
        `;
    }

    private addDuplicateAnyway() {
        this.pointsAlreadyOnMap = [];
        this.isAlreadyOnMap = false;
    }

    private sendToUpvote() {
        this.dispatchEvent(new CustomEvent('go-to-upvote-point', {
            detail: {
                point: this.pointsAlreadyOnMap[0],
            },
            bubbles: true,
        }));
    }

    renderAddAnotherButton(topicData: any) {
    return topicData.topic?.add_point_customization?.show_add_another !== false && !this.isNominationPage ? html`
        <form-button @click="${async () => await this.addAnother()}" class="success">
            <translated-span>Add Another ${topicData.topic?.point_name ?? 'Point'}</translated-span>
        </form-button>
        <br /><br />
        ` : '';
    }

    renderEmailLinkMessage(topicData: any) {
        return isDefined(undefinedIfEmpty(getFromLocalStorage('email'))) && topicData.topic?.send_crowdsource_email !== false ? html`
            <translated-span>Check your email for a unique link that allows you to edit and update this ${topicData.topic?.point_name ?? 'point'}.</translated-span>
        ` : '';
    }

    renderCustomSuccessMessage(topicData: any) {
        return html`
            <h3><translated-span>${topicData.topic?.add_point_customization?.custom_success_message}</translated-span></h3>
            <br /><br />
            ${this.renderAddAnotherButton(topicData)}
        `;
    }

    renderApprovalRequiredMessage(topicData: any) {
        return html`
            <h3><translated-span>Thank you for your submission! Your ${topicData.topic?.point_name ?? 'point'} will be viewable to the public as soon
            as the map administrator approves it.</translated-span></h3>
            <br /><br />
            ${this.renderAddAnotherButton(topicData)}
            ${this.renderEmailLinkMessage(topicData)}
        `;
    }

    renderNoApprovalRequiredMessage(topicData: any) {
        return html`
            <h3><translated-span>Thank you for your submission!</translated-span></h3>
            <br /><br />
            ${this.renderAddAnotherButton(topicData)}
            ${this.renderEmailLinkMessage(topicData)}
        `;
    }

    renderSuccessMessage(topicData: any) {
        if (isDefined(undefinedIfEmpty(topicData.topic?.add_point_customization?.custom_success_message))) {
            return this.renderCustomSuccessMessage(topicData);
        } else if (topicData.topic?.requires_approval === true || featuresInfo.pointsPerMapEnabled < pointCache.allPoints.length) {
            return this.renderApprovalRequiredMessage(topicData);
        } else {
            return this.renderNoApprovalRequiredMessage(topicData);
        }
    }

    renderSuccess() {
        this.rotateValue = 0;
        this.rotatePreviewCSS = '';

        if (this.editMode) {
            return html`<div class="success_message"><h3><translated-span>Updated successfully!</translated-span></h3></div>`;
        }

        if (((topicData.topic?.requires_approval) ?? false) && !((this.isWriter) ?? false)) {
            return html`
                <div class="success_message">
                    ${this.renderSuccessMessage(topicData)}
                </div>
            `;
        }

        // Only have success message for crowdsourcing, not for admin
        if (!((this.isWriter) ?? false)) {
            // if a pop up is coming do not show the success message
            if (isDefined(topicData.postPointSubmissionPopUp)) {
                return html``;
            }

            return html`
                <div class="success_message">
                    ${this.renderSuccessMessage(topicData)}
                </div>
            `;
        } else {
            return html`
                <div class="success_message">
                    <h3>Added!</h3>
                </div>
            `;
        }
    }

    async getTranslatedCustomField(originalField: CustomFieldCustomization): Promise<{ originalField: CustomFieldCustomization, translatedField: CustomFieldCustomization, options?: Array<{text: string, value:string}>} | undefined> {
        if (!translationData.isReady || !translationData.isActivated) {
            return undefined;
        }
        const customizations: AddPointFieldCustomization = {
            ...originalField.customizations,
            header: originalField.customizations.header ? await translationData.translatePhrase(originalField.customizations.header) : originalField.customizations.header,
            sub_header: originalField.customizations.sub_header ? await translationData.translatePhrase(originalField.customizations.sub_header) : originalField.customizations.sub_header
        };

        // if CustomFieldMultiSelectType or CustomFieldSingleSelectType translate the options
        let translatedFieldType = originalField.field_type;
        let options: Array<{text: string, value:string}> | undefined;

        if ('MultiSelect' in originalField.field_type) {
            const originalOptions = originalField.field_type.MultiSelect.options;
            const translatedOptions = await translationData.translatePhrases(originalOptions, translationData.language);
            options = translatedOptions.map((translation) => ({text: translation.translated_text, value: translation.original_text}));


            translatedFieldType = {
                MultiSelect: {
                    ...originalField.field_type.MultiSelect,
                    options: translatedOptions.map((options) => options.translated_text)
                }
            };
        } else if ('SingleSelect' in originalField.field_type) {
            const originalOptions = originalField.field_type.SingleSelect.options;
            const translatedOptions = await translationData.translatePhrases(originalOptions, translationData.language);
            options = translatedOptions.map((translation) => ({text: translation.translated_text, value: translation.original_text}));

            translatedFieldType = {
                SingleSelect: {
                    ...originalField.field_type.SingleSelect,
                    options: translatedOptions.map((options) => options.translated_text)
                }
            };
        }

        const translatedField: CustomFieldCustomization = {
            ...originalField,
            customizations,
            field_type: translatedFieldType
        };

        return {originalField, translatedField, options};
    }

    render() {
        if (this.success) { return this.renderSuccess(); }

        return html`
            <div class="addEditPointFlexContainer" style="font-family: ${topicData.topic?.custom_font ?? 'Montserrat'}">
                ${this.uploadInProgress ? html`
                    <div class="resultDialogContainer">
                        <div><translated-span>Creating ${topicData.topic?.point_name ?? 'Point'} and Uploading Images...</translated-span></div>
                        <vaadin-progress-bar indeterminate></vaadin-progress-bar>
                    </div>
                `: ''}
                ${this.validationInProgress ? html`
                    <div class="resultDialogContainer">
                        <div><translated-span>Validating ${topicData.topic?.point_name ?? 'Point'}...</translated-span></div>
                        <vaadin-progress-bar indeterminate></vaadin-progress-bar>
                    </div>
                `: ''}
                ${this.hideTitle ? '' : html`<h2><translated-span>${this.editMode ? `${topicData.topic?.point_name ?? 'Point'} Edit Form` : `${topicData.topic?.point_name ?? 'Point'} Form`}</translated-span></h2>`}
                <div id="container" class="modalContent">
                    <div class="wide inputSection horizontal-flex">
                        <div class="inlineDiv" style="flex-grow: 1;">
                            <div class="${this.customShape ? 'hidden': ''}" style="width: 100%;">
                                <h5 class="header" style="margin-bottom:0.5rem;"><translated-span>${topicData.topic?.add_point_customization?.location?.header ?? 'Search for a Place'}</translated-span></h5>
                                ${topicData.topic?.add_point_customization?.location?.sub_header ?
                                    html`<span class="caption"><translated-span>${topicData.topic?.add_point_customization?.location?.sub_header}</translated-span></span>`
                                : ''}
                                <div class="inlineDiv">
                                    <proxi-input-place
                                        id="location"
                                        class="input"
                                        style="font-family: ${topicData.topic?.custom_font ?? 'Montserrat'}"
                                        placeholder="${this.componentNames.locationPlaceholder}"
                                        value="${this.point?.location}"
                                        @value-changed="${this.locationChanged}"
                                        required>
                                    </proxi-input-place>
                                </div>
                                ${topicData.topic?.is_addpointer === true && isDefined(topicData.mapComponent) ? html`
                                    <translated-span style="font-size: 0.7rem; color: gray; margin-top: 5px;display: block; line-height: 0.75rem;">
                                        Tip: In addition to searching, you can also close this form then double-click anywhere on the map to add a point, or you can enter specific coordinates in the search box above (e.g. 34.0522, -118.2437).
                                    </translated-span>
                                ` : ''}
                                ${this.dragEndView ? html`
                                    <input
                                        class="form-check-input"
                                        id="keepPreviousPlaceDetailsCheckbox"
                                        type="checkbox"
                                        @change="${this.updateDragLocationWithPreviousDetails}"/>
                                    <label class="emailNotification" for="keepPreviousPlaceDetailsCheckbox">
                                        <translated-span>Keep Details and Google Photos for <i>${this.locationBeforeDrag?.search}</i></translated-span>
                                    </label>
                                ` : ''}
                                ${topicData.challengeHappeningNow && this.editMode ? html`
                                    <div class="challengewarning" style="display: flex; align-items: center; gap: 5px; margin-top: 0.5rem; color: var(--main-invalid-color);">
                                        <mwc-icon>warning</mwc-icon>
                                        <translated-span>There is a challenge happening right now. Changing the location of this point will impact check-in codes and users' challenge progress.</translated-span>
                                    </div>
                                ` : ''}
                            </div>
                            <div class="${this.customShape ? '': 'hidden'}" style="flex:1;">
                                <h5 class="header" style="display:flex; align-items:center; gap:5px">
                                    <mwc-icon>${getShapeIconName(isDefined(this.customShape?.shape.circle_details) ? 'Circle' : this.customShape?.shape?.geojson?.type ?? 'LineString')}</mwc-icon>
                                    <translated-span>Shape set for ${topicData.topic?.point_name ?? 'Point'}</translated-span>
                                </h5>

                                <form-button size="xs" @click="${this.toggleShapeEditor}" class="form-button">${this.showShapeEditor ? 'Hide Editor' : 'Edit Shape'}</form-button>
                                ${this.showShapeEditor && this.customShape ? html`
                                    <div style="width: 100%; border: 1px solid black;padding: 5px;border-radius: 4px;">
                                        <edit-shape-modal
                                            id="shapeStyleEditor"
                                            .focusedShape=${this.customShape}
                                            @close=${() => {this.showShapeEditor = false;}}
                                            .isAddEditPointMode=${true}
                                            ></edit-shape-modal>
                                    </div>
                                ` : ''}
                            </div>
                        </div>
                    </div>
                    ${this.isNominationPage && this.isAlreadyOnMap ? this.renderAlreadyOnMapNomination() : ''}
                    <div class="wide inputSection">
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;">
                            ${this.oneCategoryAndSet ? '' : html`
                                <div @click=${() => {this.activeTab = 'category';}} style="cursor: pointer;">
                                    <translated-span class="${this.activeTab === 'category' ? 'header' : 'tabButton'}">${topicData.topic?.add_point_customization?.category?.header ?? 'Set Category'}</translated-span>
                                </div>
                            `}
                            <div class="tabs">
                                ${topicData.topic?.enable_point_numbering === true && (this.isWriter || this.editMode)  ? html`
                                    <div class='admin-button'>
                                        <form-tooltip
                                            tipwidth="180px"
                                            leftPadding="30%"
                                            style="font-size: 0.75rem; white-space: normal;"
                                            message="${this.isWriter ? `As an admin, you may assign this ${topicData.topic.point_name ?? 'point'} a number.` : `Only admins can edit the numbers`}">
                                            <translated-span ?disabled=${!this.isWriter} class="tabButton ${this.activeTab === 'number' ? 'active' : ''}" @click=${() => {this.activeTab = 'number';}}>Number</translated-span>
                                        </form-tooltip>
                                    </div>
                                ` : ''}
                                ${this.isWriter && topicData.hasExceptions !== IntegrationException.WelcomeHomeRelocations && this.userDrawnShapeGeometry === undefined && this.customShape === undefined ? html`
                                    <div class='admin-button'>
                                    <form-tooltip
                                        tipwidth="180px"
                                        leftPadding="30%"
                                        style="font-size: 0.75rem; white-space: normal;text-decoration:none"
                                        message="As an admin, you may give this point a custom icon.">
                                        <translated-span class="tabButton ${this.activeTab === 'icon' ? 'active' : ''}" @click=${() => {this.activeTab = 'icon';}}>Custom Icon </translated-span>
                                    </form-tooltip>
                                    </div>
                                ` : ''}
                            </div>
                        </div>
                        <div class="tabContent" style="display: ${this.activeTab === 'category' ? 'block' : 'none'}">
                            ${this.oneCategoryAndSet ? '' : html`
                                ${topicData.topic?.add_point_customization?.category?.sub_header ?
                                    html`<translated-span class="caption">${topicData.topic?.add_point_customization?.category?.sub_header}</translated-span>`
                                : ''}
                                <category-selector
                                    id="categorySelector"
                                    ?canAddCategory=${this.customShape === undefined}
                                    .dropdown=${this.editMode || topicData.categories.length > 8}
                                    .previousValue=${this.topicCategoryId}
                                    .previewMode=${this.previewMode}
                                    @select=${this.categorySelected}>
                                </category-selector>
                                ${topicData.categories.filter((category) => isDefined(undefinedIfEmpty(category.description)) && category.description !== TEMPLATE_CATEGORIES[0].description && category.description !== 'This is a auto-generated topic').map((cat) => html`
                                    <p class="additionalInfoText ${this.topicCategoryId === cat._id?.$oid ? '' : 'hidden'}">
                                        <translated-span class="additionalInfoText">${cat.description}</translated-span>
                                    </p>
                                `)}
                            `}

                        </div>
                        <div class="tabContent" style="display: ${this.activeTab === 'number' ? 'block' : 'none'}">
                            <div class="flexContainer" style="flex-direction: column; align-items: center;">
                                <div>
                                    <proxi-textfield
                                        id="number"
                                        type="text"
                                        placeholder="e.g. 1,2,A,B5"
                                        maxLength="8"
                                        ?disabled=${!this.isWriter}
                                        @change="${() => {this.number=this.numberElement?.value;this.needsMarkerChange = true;}}"
                                        hideMaxLength>
                                    </proxi-textfield>
                                    ${this.allNumbersCollection.length > 0 && this.isWriter ? html`
                                        <form-tooltip
                                            tipwidth="150px"
                                            position="top"
                                            style="font-size: 0.7rem;line-height: 0.7rem"
                                            leftPadding="30%"
                                            message="So far, you have assigned these numbers to ${topicData.topic?.point_name ?? 'point'}s: ${this.allNumbersCollection.join(', ')}.">
                                            <translated-span class="additionalInfoText" style="font-size: 0.7rem;line-height: 0.7rem;white-space: normal; max-height: 3rem; overflow: hidden; text-overflow: ellipsis;">
                                                You've already used numbers: ${this.allNumbersCollection.join(', ')}
                                            </translated-span>
                                        </form-tooltip>
                                    ` : ''}
                                </div>
                            </div>
                        </div>
                        <div class="tabContent" style="display: ${this.activeTab === 'icon' ? 'block' : 'none'}">
                            <translated-span class="additionalInfoText">For best results, upload a square shaped file. A smaller file size helps the map load faster.</translated-span>
                            ${this.custom_icon_url && this.custom_icon_url !== '' ? html`
                                <div class="imagePreviewContainer">
                                    <div class="imagePreview" style="display: flex; align-items: center; flex-direction: row; justify-content: center;">
                                        <img src="${this.custom_icon_url}" alt="Custom Icon" style="width:30px;height:30px">
                                        <div class="imagePreviewControls">
                                            <mwc-icon-button @click="${() => {this.custom_icon_url = undefined;}}" icon="delete"></mwc-icon-button>
                                        </div>
                                    </div>

                                </div>
                            ` : html`
                                <image-upload-component
                                    autoUpload
                                    .imageUrl="${this.custom_icon_url ?? ''}"
                                    @retreived-autoupload-url="${this.onUploadCustom}"
                                ></image-upload-component>
                            `}
                        </div>
                    </div>
                    ${(checkTagsFieldVisibility(topicData.topic, this.currentValues) || this.isWriter) && topicData.topic?.tag_info.user_tags && topicData.topic?.tag_info.user_tags.length > 0 && !this.exceptionHideCustomFields ? html`
                    <div class="wide inputSection">
                        ${this.adminVisibilityNote(checkTagsFieldVisibility(topicData.topic, this.currentValues), 'tags')}
                        <div class="inlineDiv">
                            <h5 class="header"><translated-span>${topicData.topic?.add_point_customization?.tags?.header ?? `Tags`}</translated-span></h5>
                            ${this.isWriter ? html`
                            `: ''}
                        </div>
                        <translated-span class="additionalInfoText">${topicData.topic?.add_point_customization?.tags?.sub_header ?? ''}</translated-span>
                        <tags-selector
                            id="tagsSelector"
                            ?rerenderTags=${this.rerenderTags}
                            ?isWriter="${this.isWriter}"
                            .selectedTags="${this.selectedTags}"
                            .enableTranslate="${true}"
                            @add-new-tag=${this.addNewTag}
                            @remove-tag=${this.removeTag}
                        ></tags-selector>
                    </div>
                    ` : ''}
                    ${checkTitleFieldVisibility(topicData.topic, this.currentValues) || (this.isWriter && topicData.hasExceptions !== IntegrationException.WelcomeHomeRelocations) ? html`
                        <div class="wide inputSection">
                            ${this.adminVisibilityNote(checkTitleFieldVisibility(topicData.topic, this.currentValues))}
                            <proxi-textfield
                                id="name"
                                type="text"
                                @input="${() => this.requestUpdate()}"
                                ?required=${topicData.topic?.add_point_customization?.title?.mandatory ?? false}
                                label="${this.componentNames.titleLabel}"
                                placeholder="${topicData.topic?.add_point_customization?.title?.sub_header ?? ''}"
                                maxLength="80">
                            </proxi-textfield>
                        </div>
                    ` : ''}
                    <!-- Rich text editor for description -->
                    ${checkDescriptionFieldVisibility(topicData.topic, this.currentValues) || this.isWriter ? html`
                        <div class="wide inputSection">
                            ${this.adminVisibilityNote(checkDescriptionFieldVisibility(topicData.topic, this.currentValues))}
                            <div style="display: flex; align-items: center; justify-content: space-between;">
                                <h5 class="header"><translated-span>${topicData.topic?.add_point_customization?.description?.header ?? `Description`}</translated-span></h5>
                                ${this.descriptionElement?.root?.querySelector('a') !== null ? html`
                                    <div style="display: flex; align-items: center; justify-content: flex-end;flex-wrap:wrap;">
                                        <form-switch
                                            id="descriptionPreviewSwitch"
                                            name="descriptionPreviewSwitch"
                                            style="flex: unset;"
                                            ?checked=${this.descriptionPreviewMode}
                                            @change="${this.descriptionPreviewMode ? this.turnOffDescriptionPreviewMode : this.turnOnDescriptionPreviewMode}">
                                        </form-switch>
                                        <span class="additional infoText" style="margin-left: 0.2rem;"><translated-span>Preview with Embeds</translated-span></span>
                                    </div>
                                ` : ''}
                            </div>
                            ${topicData.topic?.add_point_customization?.description?.sub_header ?
                                html`<span class="caption"><translated-span>${topicData.topic?.add_point_customization?.description?.sub_header}</translated-span></span>`
                            : ''}
                            <rich-text-editor id="description" style="margin-top: 0.25rem; ${this.descriptionPreviewMode ? 'display:none' : ''}" ?showWarning=${this.isWriter}></rich-text-editor>
                            ${this.descriptionPreviewMode ? html`
                                <div class="previewText" style="margin-top: 0.25rem; padding: 0.5rem; border: 1px solid #e0e0e0;">
                                    ${this.previewDescription}
                                </div>
                            ` : ''}
                        </div>
                    ` : ''}
                    ${!((topicData.topic?.has_dates) ?? false) || (!(checkDateTimeFieldVisibility(topicData.topic, this.currentValues)) && !this.isWriter)
                        ? ''
                        : html`
                        <div class="wide inputSection">
                            ${this.adminVisibilityNote(checkDateTimeFieldVisibility(topicData.topic, this.currentValues))}
                            ${isDefined(topicData.topic?.add_point_customization?.date_time?.header) ? html`
                                <h4 class="input-label"><translated-span>${topicData.topic?.add_point_customization?.date_time?.header}</translated-span></h4>
                            ` : ''}
                            ${isDefined(topicData.topic?.add_point_customization?.date_time?.sub_header) ? html`
                                <span class="caption"><translated-span>${topicData.topic?.add_point_customization?.date_time?.sub_header}</translated-span></span>
                            ` : ''}
                            <date-time-range-picker
                                id="dateTimeRangePicker"
                                class="${this.exceptionHideCustomFields ? 'hidden' : ''}"
                                .timeOnly=${topicData.topic?.has_time_only ?? false}
                                .dateOnly=${topicData.topic?.has_date_only ?? false}
                                .timezoneShorthand=${this.timezoneShortName ?? ''}
                                .showEndDateTime=${topicData.topic?.has_end_date_time ?? true}
                                ?filterPast=${((topicData.topic?.should_filter_past_events) ?? false) && !this.editMode}
                                ?clearEnabled=${false}
                                ?required=${topicData.topic?.add_point_customization?.date_time?.mandatory ?? false}
                                @change="${() => { void this.refreshTimeRangePreview(); }}">
                            </date-time-range-picker>
                            ${this.timezoneId !== undefined || this.timeRangePreview !== undefined ? html`
                                <div class="caption tz-preview-note">
                                    ${this.timeRangePreview !== undefined ? html`
                                        <div>Will read as: <b>${this.timeRangePreview}</b></div>
                                    ` : ''}
                                </div>
                            ` : ''}
                        </div>`
                    }
                    ${(topicData.topic?.add_point_customization?.custom_fields?.length ?? 0) === 0 || this.exceptionHideCustomFields ? '' : html`
                            ${this.translatedCustomFields?.map((customField) => {
                                const originalField = customField.originalField;
                                const translatedField = customField.translatedField;
                                const options = customField.options ?? undefined;
                                if (!this.isCustomFieldRendered(originalField)) {
                                    return '';
                                }
                                const customFieldVisible = checkCustomFieldVisibility(originalField,this.currentValues);
                                return html`
                                ${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations ? '' : this.adminVisibilityNote(customFieldVisible)}
                                ${'Text' in originalField.field_type ? html`
                                    <div class="wide inputSection">
                                        <proxi-textfield
                                            .id="id${originalField._id?.$oid}"
                                            type="text"
                                            @change="${() => this.requestUpdate()}"
                                            ?required=${originalField.customizations.mandatory ?? false}
                                            value=${ifDefined(this.pointOwnFieldValue(originalField._id?.$oid))}
                                            .label="${translatedField?.customizations.header ?? originalField.customizations.header ?? 'Text'}"
                                            .placeholder="${this.listingFieldPlaceholder(originalField._id?.$oid) ?? translatedField?.customizations.sub_header ?? originalField.customizations.sub_header ?? ''}">
                                        </proxi-textfield>
                                    </div>
                                ` :
                                'Number' in originalField.field_type ? html`
                                    <div class="wide inputSection">
                                        <proxi-textfield
                                            .id="id${originalField._id?.$oid}"
                                            type="number"
                                            step="any"
                                            @change="${() => this.requestUpdate()}"
                                            ?required=${originalField.customizations.mandatory ?? false}
                                            value=${ifDefined(this.pointOwnFieldValue(originalField._id?.$oid))}
                                            .label="${translatedField?.customizations.header ?? originalField.customizations.header ?? 'Number'}"
                                            .placeholder="${this.listingFieldPlaceholder(originalField._id?.$oid) ?? translatedField?.customizations.sub_header ?? originalField.customizations.sub_header ?? ''}">
                                        </proxi-textfield>
                                    </div>
                                ` :
                                'Phone' in originalField.field_type ? html`
                                    <div class="wide inputSection">
                                        <proxi-textfield
                                            .id="id${originalField._id?.$oid}"
                                            type="tel"
                                            inputmode="tel"
                                            pattern="^(?=(?:\D*\d){10,}\D*$)\+?[\d\s().-]+$"
                                            @input="${(e: Event) => {
                                                const target = e.target as ProxiTextfield;
                                                const formatted = formatPhoneNumberAsUserTypes(target.value);
                                                if (formatted !== target.value) {
                                                    target.value = formatted;
                                                }
                                            }}"
                                            @change="${() => this.requestUpdate()}"
                                            ?required=${originalField.customizations.mandatory ?? false}
                                            value=${ifDefined(this.pointOwnFieldValue(originalField._id?.$oid))}
                                            .label="${translatedField?.customizations.header ?? originalField.customizations.header ?? 'Phone Number'}"
                                            .placeholder="${this.listingFieldPlaceholder(originalField._id?.$oid) ?? translatedField?.customizations.sub_header ?? originalField.customizations.sub_header ?? ''}">
                                        </proxi-textfield>
                                    </div>
                                ` :
                                'URL' in originalField.field_type ? html`
                                    <div class="wide inputSection">
                                        <proxi-textfield
                                            .id="id${originalField._id?.$oid}"
                                            type="url"
                                            @change="${(e: Event) => {validateUrl((e.target as HTMLInputElement).value, {allowEmpty: true, showToast: true}); this.requestUpdate(); }}"
                                            ?required=${originalField.customizations.mandatory ?? false}
                                            value=${ifDefined(this.pointOwnFieldValue(originalField._id?.$oid))}
                                            .label="${translatedField?.customizations.header ?? originalField.customizations.header} (include https://)"
                                            .placeholder="${this.listingFieldPlaceholder(originalField._id?.$oid) ?? translatedField?.customizations.sub_header ?? originalField.customizations.sub_header ?? ''}">
                                        </proxi-textfield>
                                    </div>
                                ` :
                                'MultiSelect' in originalField.field_type ?
                                    html`
                                    <div class="wide inputSection">
                                        <h5 class="header">${translatedField?.customizations.header ?? originalField.customizations.header ?? 'Select an Option'}</h5>
                                        <proxi-multi-selectfield
                                            .id="id${originalField._id?.$oid}"
                                            outlined
                                            style="width: 100%; margin-top: 10px;"
                                            @change="${() => this.requestUpdate()}"
                                            title="Select Options"
                                            .options=${options?.map((option) => ({ text: option.text, value: option.value })) ?? []}
                                            .selectedValues=${this.selectedOptions.get(originalField._id?.$oid ?? '')?.map((value: string) => ({ text: options?.find((option) => option.value === value)?.text ?? value, value })) ?? []}
                                            @selection-changed=${async (e: CustomEvent) => {
                                                const selectedValues = e.detail.selectedValues.map((item: any) => item.value);
                                                this.selectedOptions.set(originalField._id?.$oid ?? '', selectedValues);
                                            }}
                                            ?required=${originalField.customizations.mandatory ?? false}>
                                        </proxi-multi-selectfield>
                                    </div>
                                ` :
                                'SingleSelect' in originalField.field_type ? html`
                                    <div class="wide inputSection">
                                        <h5 class="header">${translatedField?.customizations.header ?? originalField.customizations.header ?? 'Select an Option'}</h5>
                                        <proxi-selectfield
                                            .id="id${originalField._id?.$oid}"
                                            outlined
                                            @change="${() => this.requestUpdate()}"
                                            value=${ifDefined(this.pointOwnFieldValue(originalField._id?.$oid))}
                                            style="width: 100%; margin-top: 10px;"
                                            label="Select an Option"
                                            ?required=${originalField.customizations.mandatory ?? false}>
                                            ${originalField.field_type.SingleSelect.options.map((option) => html`
                                                <mwc-list-item value="${option}">${options?.find((options) => options.value === option)?.text ?? option}</mwc-list-item>
                                            `)}
                                        </proxi-selectfield>
                                    </div>
                                ` :
                                'Email' in originalField.field_type ? html`
                                    <div class="wide inputSection">
                                        <proxi-textfield
                                            .id="id${originalField._id?.$oid}"
                                            type="email"
                                            @change="${() => this.requestUpdate()}"
                                            ?required=${originalField.customizations.mandatory ?? false}
                                            value=${ifDefined(this.pointOwnFieldValue(originalField._id?.$oid))}
                                            .label="${translatedField?.customizations.header ?? originalField.customizations.header ?? 'Email'}"
                                            .placeholder="${this.listingFieldPlaceholder(originalField._id?.$oid) ?? translatedField?.customizations.sub_header ?? originalField.customizations.sub_header ?? 'Enter your email'}">
                                        </proxi-textfield>
                                    </div>
                                ` :
                                'Rating' in originalField.field_type ? html`
                                    <div class="wide inputSection">
                                        <label class="rating-label">${translatedField?.customizations.header ?? originalField.customizations.header}${originalField.customizations.mandatory === true ? ' *' : ''}</label>
                                        <div style="display: flex; align-items: center; gap: 8px;">
                                            <input
                                                type="range"
                                                id="id${originalField._id?.$oid}"
                                                min="0"
                                                max="${('Rating' in originalField.field_type ? originalField.field_type.Rating?.maxStars : undefined) ?? 5}"
                                                step="0.5"
                                                value=${this.pointOwnFieldValue(originalField._id?.$oid) ?? '0'}
                                                ?required=${originalField.customizations.mandatory ?? false}
                                                style="flex: 1; accent-color: #FFB400;"
                                                @input=${() => this.requestUpdate()}
                                                @change=${() => this.requestUpdate()}>
                                            <span style="font-size: 0.9rem; font-weight: 500; color: var(--main-primary-color, #333); min-width: 2.5em; text-align: center;">
                                                ${this.shadowRoot?.querySelector<HTMLInputElement>(`#id${originalField._id?.$oid ?? ''}`)?.value ?? '0'}/${('Rating' in originalField.field_type ? originalField.field_type.Rating?.maxStars : undefined) ?? 5}
                                            </span>
                                        </div>
                                        <div style="display: flex; line-height: 1; font-size: 1.3rem; gap: 1px; margin-top: 4px;">
                                            ${(() => {
                                                const ms = ('Rating' in originalField.field_type ? originalField.field_type.Rating?.maxStars : undefined) ?? 5;
                                                const val = parseFloat(this.shadowRoot?.querySelector<HTMLInputElement>(`#id${originalField._id?.$oid ?? ''}`)?.value ?? '0');
                                                return Array.from({ length: ms }, (_, i) => {
                                                    const isFull = val >= i + 1;
                                                    const isHalf = !isFull && val >= i + 0.5;
                                                    return html`<span style="position: relative; display: inline-block; width: 1em; height: 1em;">
                                                        <span style="color: #D3D3D3;">★</span>
                                                        <span style="position: absolute; inset: 0; color: #FFB400; overflow: hidden; width: ${isFull ? '100%' : isHalf ? '50%' : '0%'};">★</span>
                                                    </span>`;
                                                });
                                            })()}
                                        </div>
                                        ${originalField.customizations.sub_header ? html`<span style="font-size: 0.8rem; color: grey;">${translatedField?.customizations.sub_header ?? originalField.customizations.sub_header}</span>` : ''}
                                    </div>
                                ` : ''}
                            `;})}
                    `}

                    ${checkMoreInfoFieldVisibility(topicData.topic, this.currentValues) || this.isWriter ? html`
                    <div class="wide inputSection">
                        ${this.adminVisibilityNote(checkMoreInfoFieldVisibility(topicData.topic, this.currentValues))}
                        <proxi-textfield
                            id="url"
                            type="text"
                            @change="${() => this.requestUpdate()}"
                            ?required=${topicData.topic?.add_point_customization?.more_info?.mandatory ?? false}
                            label="${this.componentNames.moreInfoLabel}"
                            placeholder="${this.componentNames.moreInfoPlaceholder}"
                            >
                        </proxi-textfield>
                    </div>
                    ` : ''}
                    ${checkSpecialOfferFieldVisibility(topicData.topic, this.currentValues) || (this.isWriter && topicData.hasExceptions !== IntegrationException.WelcomeHomeRelocations) ? html`
                        <div class="wide inputSection">
                            ${this.adminVisibilityNote(checkSpecialOfferFieldVisibility(topicData.topic, this.currentValues))}
                            <div class="inlineDiv">
                                <h5 class="header"><translated-span>${topicData.topic?.add_point_customization?.special_offer?.header ?? 'Special Offer'}</translated-span></h5>
                                <form-switch
                                    id="specialOfferSwitch"
                                    name="specialOfferSwitch"
                                    ?checked=${this.hasSpecialOffer}
                                    @change="${this.toggleSpecialOffer}">
                                </form-switch>
                            </div>
                        ${this.hasSpecialOffer === true ? html`
                            <proxi-textarea
                                id="special_offer_description"
                                style="width:100%; margin-top:5px"
                                type="text"
                                outlined
                                charCounter="internal"
                                ?required=${topicData.topic?.add_point_customization?.special_offer?.mandatory ?? false}
                                value=${this.point?.special_offer?.description ?? ''}
                                placeholder="${this.componentNames.specialOfferPlaceholder}"
                                maxLength="1000">
                            </proxi-textarea>
                        `: ''}
                        </div>
                    ` : ''}
                    ${checkPhotosFieldVisibility(topicData.topic, this.currentValues) || (this.isWriter && topicData.hasExceptions !== IntegrationException.WelcomeHomeRelocations) ? html`
                        <div class="wide inputSection">
                            ${this.adminVisibilityNote(checkPhotosFieldVisibility(topicData.topic, this.currentValues))}
                            <h5 class="header"><translated-span>${topicData.topic?.add_point_customization?.photos?.header ?? 'Upload Photos'}</translated-span></h5>
                            ${topicData.topic?.add_point_customization?.photos?.sub_header ?
                                html`<translated-span class="caption">${topicData.topic?.add_point_customization?.photos?.sub_header}</translated-span>`
                            : ''}
                            <div class="photoSectionContainer" @drop="${this.onDrop}" @dragover="${this.preventDefault}" @dragenter="${this.preventDefault}">
                                <div class="dropLabelContainer">
                                    <form-button size="sm" class="uploadButton" borderStyle="round" @click="${this.onAddFilesClick}">
                                        <mwc-icon style="margin-right: 0.25rem">upload</mwc-icon>
                                        <translated-span>Upload</translated-span>
                                    </form-button>
                                    <div class="dropLabel"><translated-span>or drag them in</translated-span></div>
                                </div>
                                <div class="imageContainer">
                                    ${this.existingImages.map(
                                        (image) => html`
                                            <div class="image-container">
                                                <div class="buttonContainer">
                                                    <mwc-icon-button
                                                        class="imageButton"
                                                        @click="${async () => await this.handleExistingDelete(image)}"
                                                        raised
                                                        icon="close"
                                                    ></mwc-icon-button>
                                                </div>
                                                <img src="${image.sas_url}" class="gallery-item" />
                                            </div>
                                        `
                                    )}
                                    ${this.uploadedImages.map(
                                        (image) => html`
                                            <div class="image-container">
                                                <div class="buttonContainer">
                                                    <mwc-icon-button
                                                        class="imageButton"
                                                        @click="${async () =>
                                                            await this.handleRotate(image)}"
                                                        raised
                                                        icon="autorenew"
                                                    ></mwc-icon-button>
                                                    <mwc-icon-button
                                                        class="imageButton"
                                                        @click="${async () =>
                                                            await this.handleClose(image.file)}"
                                                        raised
                                                        icon="close"
                                                    ></mwc-icon-button>
                                                </div>
                                                <img
                                                    src="${URL.createObjectURL(image.file)}"
                                                    class="gallery-item"
                                                    style="transform:${this.rotatePreviewCSS}"
                                                />
                                            </div>
                                        `
                                    )}
                                </div>
                                <input
                                    type="file"
                                    id="fileInput"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    @change="${this.onFileInputChange}"
                                />
                            </div>
                        </div>
                        ${topicData.isWriter ? html`
                            <span class="image-upload-component__sizing-guide" @click="${() => window.open(ImageSizingGuide, '_blank')}}" style="display: block; text-align: center; margin-top: 0; width: 100%;margin-bottom: 10px;">
                                <a href="${ImageSizingGuide}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline; color: var(--main-primary-color); cursor: pointer; font-size: 10px;">
                                    <translated-span>View Our Image Sizing Guide</translated-span>
                                </a>
                            </span>
                        ` : ''}
                    ` : ''}
                    ${checkEmailFieldVisibility(topicData.topic, this.currentValues) && !this.isWriter ? html`
                        <div class="wide inputSection" ?hidden="${this.isWriter}">
                            <h5 class="header"><translated-span>${topicData.topic?.add_point_customization?.email?.header ?? 'Your Email Address'}</translated-span></h5>
                            ${topicData.topic?.add_point_customization?.email?.sub_header ?
                              html`<span class="caption"><translated-span>${topicData.topic?.add_point_customization?.email?.sub_header}</translated-span></span>`
                            : ''}
                            ${undefinedIfEmpty(getFromLocalStorage('email')) && !this.isWriter && !this.previewMode && !this.dontSaveEmailAfterSubmit ? html`
                                <p>${this.editMode && this.point?.email ? this.point?.email : getFromLocalStorage('email')}
                                    <span href="Remove" @click="${() => {removeLocalStorage('email'); this.requestUpdate();}}" style="cursor: pointer; color: var(--main-invalid-color); text-decoration: underline; margin-left: 1rem;">
                                    Change</span>
                                </p>
                            ` : ''}
                            <proxi-textfield
                                id="email"
                                type="email"
                                @change="${() => this.requestUpdate()}"
                                placeholder="Your Email Address"
                                value="${ifDefined(this.editMode && this.point?.email ? this.point?.email : (undefinedIfEmpty(getFromLocalStorage('email')) && !this.dontSaveEmailAfterSubmit ? getFromLocalStorage('email') : ''))}"
                                ?hidden="${isDefined(undefinedIfEmpty(getFromLocalStorage('email'))) && !this.dontSaveEmailAfterSubmit}"
                                ?required="${topicData.topic?.add_point_customization?.email?.mandatory}">
                            </proxi-textfield>
                            <span class="emailNotification" ?hidden="${
                                this.isWriter || topicData.topic?.send_crowdsource_email === false
                            }">
                            <translated-span>${undefinedIfEmpty(topicData.topic?.add_point_customization?.custom_email_warning) ?? `Submit your email to receive a secret link for editing or deleting your ${topicData.topic?.point_name ?? 'point'}`}.</translated-span>
                            </span>
                        </div>`
                    : ''}
                    ${checkTermsFieldVisibility(topicData.topic, this.currentValues) && !this.isWriter && isValidString(topicData.topic?.add_point_customization?.terms?.header) ? html`
                        <div class="wide inputSection terms">
                            <input
                                class="form-check-input"
                                id="termsAndConditionsCheckbox"
                                type="checkbox"
                                .checked="${this.termsAndConditionsChecked}"
                                @change="${(e: any) => this.termsAndConditionsSetting()}"/>
                            <label class="emailNotification" for="termsAndConditionsCheckbox">
                                <translated-span>${topicData.topic?.add_point_customization?.terms?.header}</translated-span>
                            </label>
                        </div>
                    ` : ''}
                </div>
                <error-toast id="errorToast"></error-toast>
                <div class="actionButtonGroup">
                    ${this.editMode
                        ? html`
                            <form-button size="md" variant="outlinewarning" class="button delete" @click="${() => this.deleteConfirmElement?.open()}">
                                <translated-span>Delete ${topicData.topic?.point_name ?? 'Point'}</translated-span>
                            </form-button>
                            <confirm-dialog
                                id="deleteConfirm"
                                @on-confirm="${this.delete}"
                                header="Delete?">
                                <translated-span>Are you sure you want to delete this ${topicData.topic?.point_name ?? 'point'}? This action cannot be
                                undone.</translated-span>
                            </confirm-dialog>
                            `
                        : ''
                    }
                    ${this.formProducts.length === 0 || this.exceptionHideCustomFields ? '' : html`
                        <pre-checkout-component
                            style="width: 100%; margin-bottom: 10px;"
                            id="preCheckout"
                            .product_ids=${this.visibleProductIds}
                            .organization_id=${topicData.topic?.organization_id?.$oid ?? ''}
                            .edit_view=${this.editMode}
                            .created_item_id=${this.editMode ? this.point?._id?.$oid : undefined}
                            .payment_target_type=${PaymentTargetType.Form}
                        >
                        </pre-checkout-component>
                    `}
                    <form-button size="md" class="button save" @click="${this.save.bind(
                        this
                    )}" backgroundColor=${topicData.backgroundColor} textColor=${topicData.textColor}>
                        ${((topicData.topic?.requires_approval) ?? false) && !((this.isWriter) ?? false)
                                ? html`<translated-span>Submit ${topicData.topic?.point_name ?? 'point'}</translated-span> `
                                : this.editMode ? html`<translated-span>Update ${topicData.topic?.point_name ?? 'point'}</translated-span> ` : html`<translated-span>Add ${topicData.topic?.point_name ?? 'point'}</translated-span> `}
                        ${this.preCheckoutElement && this.preCheckoutElement.remainingLineItems.length > 0 && this.preCheckoutElement.totalCostCents > 0 ? html`&nbsp;<span><translated-span>and Pay </translated-span>${this.preCheckoutElement.totalCostString}</span>` : ''}
                    </form-button>
                </div>
                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    static get styles() {
        return css`
            :host {
                font-family: var(--custom-font), 'Montserrat', sans-serif !important;
                display: flex;
            }

            .addEditPointFlexContainer {
                overflow: auto;
                display: flex;
                flex-direction: column;
                width: 100%;
            }

            h2 {
                margin-top: 10px;
                margin-bottom: 10px;
                text-align: center;
            }

            .modalContent {
                overflow-y: auto;
                overflow-x: clip;
                flex: 1;
                padding-left: 0.5rem;
            }

            .modalContent::-webkit-scrollbar {
                width: 5px;
            }

            .modalContent::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .modalContent::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .iconPreview {
                width: 36px;
                height: 36px;
                margin-right: 10px;
            }

            .input {
                width: 100%;
                padding: auto 5px;
            }

            .input.third {
                width: 32.3%;
                margin: auto 0;
                display: inline-block;
            }

            .header, label.form-label {
                font-weight: 500;
                font-size: 0.8rem;
                margin: auto 10px 5px 0px;
                white-space: nowrap;
            }

            .inlineDiv {
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
                align-items: center;
            }

            .alreadyOnMapNomination {
                margin: 1rem 0;
                padding: 1rem;
                border-radius: 4px;
                background: var(--main-invalid-color);
            }

            .caption {
                color: var(--theme-input-color);
                margin: auto;
            }

            /* Compact note under the date/time picker showing the
               point's location tz + a live preview of how the values
               will appear on the card. Slightly smaller and muted so
               it reads as helper text, not as a competing label. */
            .tz-preview-note {
                margin-top: 0.5rem;
                padding: 0.4rem 0.6rem;
                font-size: 0.8rem;
                line-height: 1.35;
                background: rgba(0, 0, 0, 0.03);
                border-left: 2px solid var(--main-primary-color, #888);
                border-radius: 2px;
            }
            .tz-preview-note > div + div {
                margin-top: 0.15rem;
            }

            .button {
                margin-left: 10px;
                margin-bottom: 0.5rem;
            }

            .button.save {
                box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
                border-radius: 10px;
            }

            .button.delete {
                --btn-background-color: ${unsafeCSS(palette.failureRed)};
            }

            .container {
                display: flex;
                flex-direction: column;
                width: 100%;
                max-width: 800px;
                min-width: 0;
            }

            .inline-icon {
                vertical-align: bottom;
                font-size: 18px !important;
            }

            [hidden] {
                display: none;
            }

            .hidden {
                display: none;
            }

            .flexContainer {
                display: flex;
                align-items: center;
            }

            .success_message {
                padding: 1rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                text-align: center;
                width: 100%
            }

            .alignEnd {
                align-items: flex-end;
            }

            .inputSection {
                --lumo-secondary-text-color: var(--theme-input-color);
                --lumo-font-size-m: 1.25rem;
                padding: 0.5rem 0;
                margin: 5px 1rem 5px 0;
                box-sizing: border-box;
            }

            .panelIcon {
                margin-right: 0.5rem;
                width: 1.25rem;
            }

            .additionalInfoText {
                color: var(--theme-input-color);
                margin-top: 0.25rem;
                margin-bottom: 0.25rem;
                font-size: 0.75rem;
                line-height: 0.75rem;
            }

            .link {
                color: var(--theme-input-color);
            }

            .folderIcon {
                --mdc-icon-size: 1rem;
                margin-right: 0.5rem;
            }

            .notificationBox {
                background: var(--theme-lightest-color);
                border-radius: 0.75rem;
                color: var(--theme-input-color);
                padding: 8px 10px;
                line-height: 150%;
                display: none;
            }

            .fieldItem {
                flex: 0 0 auto;
                margin-top: 0.5rem;
                background-color: var(--theme-lightest-color);
                border-radius: 10px;
                padding: 0.5rem 1rem 0.25rem;
            }

            .fieldItem.terms {
                max-height: 50px;
                overflow-y: scroll;
            }

            .photoSectionContainer {
                max-width: var(--modal-width);
                padding: 15px;
                border-radius: 10px;
                border: 1px dotted var(--main-moon-color);
            }

            .fieldLabel {
                color: var(--theme-input-color);
                font-size: 1rem;
                margin-bottom: 0.25rem;
            }

            .emailNotification {
                color: var(--theme-input-color);
                line-height: 14px;
                margin-top: 0.25rem;
            }

            .dropLabel {
                font-size: 1rem;
                color: var(--theme-input-color);
                margin-top: 0.5rem;
            }

            .dropLabelContainer {
                padding-bottom: 5px;
                display: flex;
                gap: 10px;
                align-items: center;
            }

            .actionButtonGroup {
                display: flex;
                flex-wrap: wrap;
                margin-top: 0.5rem;
                justify-content: flex-end;
                flex: 0 0 auto;
                border-radius: 10px;
                padding: 0.5rem 1rem 0.25rem;
            }

            .uploadButton {
                display: inline-block;
            }

            .dropLabel {
                display: inline-block;
            }

            .image-container {
                position: relative;
                display: inline-block;
                gap: 5px;
            }

            .admin-button {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap:5
            }

            .admin-visibility-note {
                display: block;
                font-size: 0.7rem;
                color: #8B6914;
                background: #FFF8E1;
                border: 1px solid #FFE082;
                border-radius: 4px;
                padding: 2px 8px;
                margin-bottom: 4px;
            }

            .gallery-item {
                height: 4rem;
                display: inline-block;
                max-width: 100%;
                border-radius: 10px;
                border: 1px solid var(--main-primary-color);
            }

            .gallery-item img {
                height: 100%;
                border-radius: 10px;
            }

            .imageButton {
                border-radius: 50%;
                background-color: var(--main-moon-color);
                --mdc-icon-button-size: 24px;
            }

            .buttonContainer {
                position: absolute;
                top: -3px;
                right: -3px;
                z-index: 1;
            }

            vaadin-date-picker {
                font-size: 1rem;
                color: var(--theme-input-color);
            }

            vaadin-time-picker {
                font-size: 1rem;
                padding-left: 4px;
            }

            form-switch {
                font-size: 0.75rem;
                flex: 1;
            }

            .switchContainer {
                margin-top: 1rem;
            }

            .subFieldLabel {
                color: var(--theme-input-color);
                font-weight: 500;
                margin-top: 0.5rem;
            }

            .resultDialogContainer {
                font-family: var(--lumo-font-family);
                color: var(--lumo-secondary-text-color);
                padding: 2rem 2rem 1rem 2rem;
                flex: 0 0 auto;
            }

            .tabContent {
                display: flex;
                flex-direction: column;
            }

            .form-label {
                font-weight: 500;
                line-height: 1rem;
                font-size: 0.8rem;
                color: var(--theme-input-color);
            }

            .tabButton {
                background-color: white;
                padding: 0.25rem;
                border-radius: 5px;
                font-family: var(--custom-font);
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 5px;
                cursor: pointer;
                text-decoration:underline;
            }

            .tabs {
                display: flex;
            }

            .horizontal-flex {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            :host([ispointpage]) {
                width: 100%;
            }

            :host([ispointpage]) .addEditPointFlexContainer {
                height: 100%;
                overflow: hidden;
            }

            :host([ispointpage]) .actionButtonGroup {
                background: white;
                border-top: 1px solid var(--theme-border-color);
                margin-top: 0;
            }

            @media only screen and (max-width: 550px) {
                .addEditPointFlexContainer {
                    min-width: 0;
                    max-height: 80vh;
                }

                /* Point page (full-screen edit form, not a modal) on mobile: let the form grow with
                   its content and rely on natural page scrolling. The 80vh cap + overflow:hidden
                   above is for modal use; in page mode it would clip the action buttons. */
                :host([ispointpage]) .addEditPointFlexContainer {
                    max-height: none;
                    height: auto;
                    overflow: visible;
                }

                /* Pin the action buttons to the bottom of the viewport so they're always reachable
                   on long forms, and don't disappear behind the mobile browser chrome. */
                :host([ispointpage]) .actionButtonGroup {
                    position: sticky;
                    bottom: 0;
                    background: white;
                    padding-bottom: env(safe-area-inset-bottom, 0px);
                    z-index: 2;
                }

                .header, label.form-label {
                    white-space: normal;
                }

                .success_message {
                    padding: 0;
                }
            }
        `;
    }
}

